var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, forwardRef, useContext, useMemo, useCallback } from 'react';
import globalStore from '../../../store';
import '../../../styles/widgets/clock/clockmanipulative.scss';
import ButtonComponent from '../../../components/ButtonComponent';
import { pointFromCenterAndAngle, selectTool } from '../../../helper/helper';
import { DataContext } from '../../../player/DataContext';
import customEventHandler from '../../../helper/customEvents';
import DigitalClock from './DigitalClock';
import CirclePartitionSVG from './CirclePartitionSVG';
import { t } from 'i18next';
import { announce } from '../../../helper/announcer';
import { useDuplicateAnnounce, useCreationAnnounce } from '../../../helper/hooks/useDuplicateAnnounce';
import RunJump from './RunJump';
import { ColorEnum } from '../../../components/ColorPicker';
import { saveState } from '../../../helper/stateManagerHelper';
import { getImageSrc } from '../../../helper/imageBlobCache';
import { useMultiSelectDragSource } from '../../../helper/hooks/useMultiSelectDrag';
import { registerMultiSelectSaveEntries } from '../../../helper/multiSelectSaveCoordinator';
// Global configuration: When true, removes runJump and digitalDisplay when elapsedTime is started
var DISABLE_RUN_JUMP_AND_DIGITAL_DISPLAY_ON_ELAPSED = false;
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var normalizeAngle = function (angle) {
    var normalized = angle % 360;
    return normalized < 0 ? normalized + 360 : normalized;
};
/**
 * Computes continuous fractional elapsed rotations from the reference.
 * Uses the known-correct integer hours + normalized angular offset on the clock face,
 * so it works even when totalMinuteAngle has been reset between drags.
 *
 * @param delta - Optional. When provided (positive=forward, negative=backward),
 *   uses the known direction instead of continuity. Pass this for large jumps
 *   (digital clock, RunJump) where prevFraction-based continuity would pick wrong.
 *   Omit for drag handler where smooth continuity is preferred.
 * Example outputs: -1.25, -0.3, 0, 0.5, 1.75
 */
var computeElapsedFraction = function (currentAngle, referenceAngle, elapsedHours, prevFraction, delta) {
    var normalizedCurrent = normalizeAngle(currentAngle);
    var normalizedRef = normalizeAngle(referenceAngle);
    // Clockwise offset from reference (0 to 1)
    var clockwiseFraction = normalizeAngle(normalizedCurrent - normalizedRef) / 360;
    // Two possible interpretations of the same position on the clock face
    var forwardResult = elapsedHours + clockwiseFraction;
    var backwardResult = elapsedHours + clockwiseFraction - 1;
    // When direction is known (digital clock, jump, run), pick the result
    // that moved in the expected direction relative to the previous fraction.
    if (delta !== undefined) {
        if (delta === 0)
            return forwardResult;
        // Check which result moved in the correct direction from prevFraction
        var forwardMoved = delta > 0 ? forwardResult > prevFraction : forwardResult < prevFraction;
        var backwardMoved = delta > 0 ? backwardResult > prevFraction : backwardResult < prevFraction;
        // If exactly one moved in the right direction, pick it
        if (forwardMoved && !backwardMoved)
            return forwardResult;
        if (backwardMoved && !forwardMoved)
            return backwardResult;
        // Both or neither: pick the one closest to expected position
        var expectedFraction = prevFraction + delta / 360;
        return Math.abs(forwardResult - expectedFraction) <= Math.abs(backwardResult - expectedFraction)
            ? forwardResult
            : backwardResult;
    }
    // For drag: pick whichever is closest to the previous fraction (smooth continuity)
    if (Math.abs(forwardResult - prevFraction) <= Math.abs(backwardResult - prevFraction)) {
        return forwardResult;
    }
    return backwardResult;
};
var toRadians = function (angle) { return ((angle - 90) * Math.PI) / 180; };
var getArcDelta = function (startAngle, endAngle) {
    var start = normalizeAngle(startAngle);
    var end = normalizeAngle(endAngle);
    return (end - start + 360) % 360;
};
var getLargeArcFlag = function (startAngle, endAngle) {
    return getArcDelta(startAngle, endAngle) > 180 ? 1 : 0;
};
var ArcPath = function (_a) {
    var startAngle = _a.startAngle, endAngle = _a.endAngle, radius = _a.radius, center = _a.center, _b = _a.bounds, bounds = _b === void 0 ? { width: 100, height: 100 } : _b, _c = _a.fullCircle, fullCircle = _c === void 0 ? false : _c, className = _a.className, stroke = _a.stroke, strokeWidth = _a.strokeWidth, opacity = _a.opacity, fill = _a.fill;
    var safeRadius = radius;
    if (fullCircle) {
        return (_jsx("circle", { className: className, cx: center.x, cy: center.y, r: safeRadius, stroke: stroke, strokeWidth: strokeWidth, opacity: opacity, fill: fill !== null && fill !== void 0 ? fill : stroke }));
    }
    var startRad = toRadians(startAngle);
    var endRad = toRadians(endAngle);
    var largeArcFlag = getLargeArcFlag(startAngle, endAngle);
    var sweepFlag = 1; // clockwise
    var x1 = center.x + safeRadius * Math.cos(startRad);
    var y1 = center.y + safeRadius * Math.sin(startRad);
    var x2 = center.x + safeRadius * Math.cos(endRad);
    var y2 = center.y + safeRadius * Math.sin(endRad);
    var path = "M ".concat(x1, " ").concat(y1, " A ").concat(safeRadius, " ").concat(safeRadius, " 0 ").concat(largeArcFlag, " ").concat(sweepFlag, " ").concat(x2, " ").concat(y2, " L ").concat(center.x, " ").concat(center.y, " Z");
    return (_jsx("path", { className: className, d: path, stroke: stroke, strokeWidth: strokeWidth, opacity: opacity, strokeLinecap: "round", fill: fill }));
};
function addTime(start, duration) {
    // Convert start time to minutes from midnight
    var startHour24 = start.hour % 12;
    if (start.period === 'PM')
        startHour24 += 12;
    var totalMinutes = startHour24 * 60 + start.minute + duration.hours * 60 + duration.minutes;
    // Wrap within 24 hours (handle negative values correctly)
    // JavaScript modulo returns negative for negative numbers, so we need to adjust
    var MINUTES_IN_DAY = 24 * 60;
    totalMinutes = ((totalMinutes % MINUTES_IN_DAY) + MINUTES_IN_DAY) % MINUTES_IN_DAY;
    // Convert back to 12-hour format
    var period = totalMinutes >= 12 * 60 ? 'PM' : 'AM';
    var hour24 = Math.floor(totalMinutes / 60);
    var minute = totalMinutes % 60;
    var hour12 = hour24 % 12;
    if (hour12 === 0)
        hour12 = 12;
    return {
        hour: hour12,
        minute: minute,
        period: period,
    };
}
var calculateAnglesFromTime = function (hour, minute, second) {
    if (second === void 0) { second = 0; }
    // Minute Hand Angle: 6 degrees per minute
    // 12 o'clock (0 minutes) is 0 degrees.
    var minuteAngle = minute * 6;
    // Hour Hand Angle: 30 degrees per hour + (minute / 60) * 30 degrees
    // We use hour_12 here (1-12)
    var hour_12 = hour === 12 ? 0 : hour; // Treat 12 as 0 for calculation
    var hourAngle = hour_12 * 30 + (minute / 60) * 30;
    // Both hands' angles are calculated based on the 12 o'clock position (0 degrees)
    // Second Hand Angle: 6 degrees per second
    var secondAngle = second * 6;
    return { hourAngle: hourAngle, minuteAngle: minuteAngle, secondAngle: secondAngle };
};
var calculateTimeFromAngles = function (hAngle, mAngle) {
    var normalizedMinuteAngle = ((mAngle % 360) + 360) % 360;
    var normalizedHourAngle = ((hAngle % 360) + 360) % 360;
    var minutes = Math.round(normalizedMinuteAngle / 6);
    // When minute rounds to 60, it means we're crossing into the next hour
    // Add 30 degrees to hour angle to advance the hour display
    var hourAdjustment = 0;
    if (minutes === 60) {
        minutes = 0;
        hourAdjustment = 30; // Advance hour by 1 (30 degrees)
    }
    var hours_12 = Math.floor((normalizedHourAngle + hourAdjustment) / 30);
    // Handle wrap-around: 0 -> 12, and >12 -> 1 (when hourAdjustment causes overflow)
    if (hours_12 === 0) {
        hours_12 = 12;
    }
    else if (hours_12 > 12) {
        hours_12 = hours_12 - 12;
    }
    return {
        hour: hours_12,
        minute: minutes,
    };
};
var AnalogClock = forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    var updateStyleOnwhiteboard = props.isOnWhiteBoard, _clockData = props._clockData;
    var analogType = _clockData.analogType, movement = _clockData.movement, hourhandVisible = _clockData.hourhandVisible, minutehandVisible = _clockData.minutehandVisible, handLabelVisible = _clockData.handLabelVisible, hour = _clockData.hour, minute = _clockData.minute, clockDesign = _clockData.clockDesign, lock = _clockData.lock, scale = _clockData.scale, id = _clockData.id, center = _clockData.center, period = _clockData.period, clockFractionOverlayParts = _clockData.fractionOverlayParts, clockFractionOverlayRotation = _clockData.fractionOverlayRotation, clockFractionOverlayColors = _clockData.fractionOverlayColors, clockFractionOverlayShadingEnabled = _clockData.fractionOverlayShadingEnabled, clockDigitalClockVisible = _clockData.digitalClockVisible, clockRunJumpVisible = _clockData.runJumpVisible, clockRunJumpTab = _clockData.runJumpTab, clockRunJumpValue = _clockData.runJumpValue, clockRuntimeSeconds = _clockData.runtimeSeconds, clockElapsedTimeDisplay = _clockData.elapsedTimeDisplay;
    // Bridge constants: derive the old flat names from elapsedTimeDisplay
    // so downstream local state / refs / effects keep working unchanged.
    var clockElapsedTimeActive = (_a = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.active) !== null && _a !== void 0 ? _a : false;
    var clockElapsedState = (_b = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.shown) !== null && _b !== void 0 ? _b : false;
    var clockElapsedDragStarted = (_c = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.dragStarted) !== null && _c !== void 0 ? _c : false;
    var clockElapsedHoursCount = (_d = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.hoursCount) !== null && _d !== void 0 ? _d : 0;
    var clockElapsedReferenceAngle = (_e = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.referenceAngle) !== null && _e !== void 0 ? _e : null;
    var clockElapsedPrevHourAngle = (_f = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.prevHourAngle) !== null && _f !== void 0 ? _f : null;
    var clockElapsedTotalMinuteAngle = (_g = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.totalMinuteAngle) !== null && _g !== void 0 ? _g : null;
    var clockElapsedFraction = (_h = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.fraction) !== null && _h !== void 0 ? _h : 0;
    /** Build a partial update that nests elapsed fields under `elapsedTimeDisplay`.
     *  `base` is the current display object; `overrides` are the fields to merge. */
    var DEFAULT_ELAPSED_DISPLAY = {
        shown: false, active: false, dragStarted: false, hoursCount: 0,
        referenceAngle: null, prevHourAngle: null, totalMinuteAngle: null, fraction: 0,
        startTime: { hourAngle: 0, minuteAngle: 0, secondAngle: 0 },
        endTime: { hourAngle: 0, minuteAngle: 0, secondAngle: 0 },
    };
    var mergeElapsed = function (base, overrides) { return (__assign(__assign({}, (base !== null && base !== void 0 ? base : DEFAULT_ELAPSED_DISPLAY)), overrides)); };
    var computedAngles = useMemo(function () { return calculateAnglesFromTime(hour, minute); }, [hour, minute]);
    var _x = useContext(DataContext), selectedTool = _x.selectedTool, setSelectedTool = _x.setSelectedTool, setManipulative = _x.setManipulative, manipulative = _x.manipulative, setActiveManipulator = _x.setActiveManipulator, setManiplulatorComponentVisible = _x.setManiplulatorComponentVisible, setSelectedManipulativeId = _x.setSelectedManipulativeId, panningEnable = _x.panningEnable, selectedManipulativeId = _x.selectedManipulativeId, color = _x.color, setContextMenu = _x.setContextMenu;
    var btnConfig = globalStore.useStoreState(function (store) { return store.jsonDataStore; }).btnConfig;
    var imageURL = btnConfig.clocks;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _y = useState(computedAngles.minuteAngle), totalMinuteAngle = _y[0], setTotalMinuteAngle = _y[1];
    var _z = useState(0), prevRawAngle = _z[0], setPrevRawAngle = _z[1];
    // Use ref for immediate synchronization to avoid stale closure issues
    var prevRawAngleRef = useRef(0);
    var _0 = useState(computedAngles.hourAngle), _hourAngle = _0[0], setHourAngle = _0[1];
    var _1 = useState(computedAngles.minuteAngle), _minuteAngle = _1[0], setMinuteAngle = _1[1];
    var _2 = useState(computedAngles.secondAngle), _secondAngle = _2[0], setSecondAngle = _2[1];
    var _3 = useState(0), seconds = _3[0], setSeconds = _3[1];
    var _4 = useState(clockElapsedTimeActive !== null && clockElapsedTimeActive !== void 0 ? clockElapsedTimeActive : false), elapsedTime = _4[0], setElapsedTime = _4[1];
    var _5 = useState(clockElapsedHoursCount !== null && clockElapsedHoursCount !== void 0 ? clockElapsedHoursCount : 0), elapsedHours = _5[0], setElapsedHours = _5[1];
    var _6 = useState(clockElapsedState !== null && clockElapsedState !== void 0 ? clockElapsedState : false), elapsedState = _6[0], setElapsedState = _6[1];
    var _7 = useState(hourhandVisible), hourhandV = _7[0], setHourhandVisible = _7[1];
    var _8 = useState(minutehandVisible), minutehandV = _8[0], setMinutehandVisible = _8[1];
    var _9 = useState(handLabelVisible), handLabelV = _9[0], sethandLabelVisible = _9[1];
    var _10 = useState(updateStyleOnwhiteboard), onWhiteBoard = _10[0], setonWhiteBoard = _10[1];
    var _11 = useState(scale || 1), scaling = _11[0], setScaling = _11[1];
    // Sync scale from props (for multi-select scaling and undo/redo)
    useEffect(function () {
        setScaling(scale || 1);
    }, [scale]);
    var _12 = useState(clockDigitalClockVisible !== null && clockDigitalClockVisible !== void 0 ? clockDigitalClockVisible : false), digitalClockVisible = _12[0], setDigitalClockVisible = _12[1];
    var _13 = useState(clockRunJumpVisible !== null && clockRunJumpVisible !== void 0 ? clockRunJumpVisible : false), runJumpVisible = _13[0], setRunJumpVisible = _13[1];
    var _14 = useState(false), secondsVisible = _14[0], setSecondsVisible = _14[1];
    var _15 = useState(false), isDraggingSelf = _15[0], setIsDraggingSelf = _15[1];
    // Local state for period to update DigitalClock in real-time during drag
    var _16 = useState(period), currentPeriod = _16[0], setCurrentPeriod = _16[1];
    // Fraction overlay state
    var _17 = useState(clockFractionOverlayShadingEnabled !== null && clockFractionOverlayShadingEnabled !== void 0 ? clockFractionOverlayShadingEnabled : true), shadingEnabled = _17[0], setShadingEnabled = _17[1];
    var _18 = useState(false), sectionMode = _18[0], setSectionMode = _18[1];
    var _19 = useState(0), focusedSectionIndex = _19[0], setFocusedSectionIndex = _19[1];
    var rotatingRef = useRef(false);
    // Get fraction overlay values from clockData, with fallback to defaults
    // Use useMemo to ensure we get the latest values when _clockData changes
    var fractionOverlayParts = useMemo(function () { return clockFractionOverlayParts !== null && clockFractionOverlayParts !== void 0 ? clockFractionOverlayParts : null; }, [clockFractionOverlayParts]);
    var fractionOverlayRotation = useMemo(function () { return clockFractionOverlayRotation !== null && clockFractionOverlayRotation !== void 0 ? clockFractionOverlayRotation : 0; }, [clockFractionOverlayRotation]);
    var fractionOverlayColors = useMemo(function () { return clockFractionOverlayColors !== null && clockFractionOverlayColors !== void 0 ? clockFractionOverlayColors : []; }, [clockFractionOverlayColors]);
    var fractionOverlayRef = useRef(null);
    var rotateStartAngleRef = useRef(0);
    var rotateInitialRotationRef = useRef(0);
    var wrapperRef = useRef(null);
    var positionDragRef = useRef(null);
    var clockRef = useRef(null);
    var runJumpRef = useRef(null);
    var hourboolRef = useRef(false);
    var minuteboolRef = useRef(false);
    var isDraggingRef = useRef(false);
    var isKeyboardActiveRef = useRef(false);
    // Refs for undo/redo - track initial values before changes
    var dragStartTimeRef = useRef(null);
    var dragStartPositionRef = useRef(null);
    var currentPositionRef = useRef(null); // Track current position during drag
    var positionDragRafRef = useRef(0);
    var fractionRotationStartRef = useRef(null);
    var currentFractionRotationRef = useRef(null); // Track current rotation during drag
    var totalMinuteAngleRef = useRef(computedAngles.minuteAngle);
    var hourAngleRef = useRef(null);
    var minuteAngleRef = useRef(null);
    // Refs for notgeared mode - track independent angles directly
    // Initialize from stored hourAngle/minuteAngle if available, otherwise calculate from hour/minute
    var notGearedHourAngleRef = useRef(_clockData.hourAngle !== undefined && _clockData.hourAngle !== 0
        ? _clockData.hourAngle
        : (hour === 12 ? 0 : hour) * 30);
    var notGearedMinuteAngleRef = useRef(_clockData.minuteAngle !== undefined && _clockData.minuteAngle !== 0
        ? _clockData.minuteAngle
        : minute * 6);
    var justFinishedDraggingRef = useRef(false);
    var elapsedReferenceMinuteAngleRef = useRef(null);
    var elapsedPrevMinuteAngleRef = useRef(null);
    var elapsedPrevHourAngleRef = useRef(null);
    var elapsedHoursRef = useRef(0);
    var elapsedFractionRef = useRef(0);
    var elapsedHoursInIntRef = useRef(0);
    // Track period (AM/PM) changes during dragging
    var periodRef = useRef(period);
    var prevHourForPeriodRef = useRef(hour);
    var isInitializingElapsedRef = useRef(false);
    // Elapsed time display: track start/end hand angles
    var elapsedStartTimeRef = useRef((_j = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.startTime) !== null && _j !== void 0 ? _j : { hourAngle: 0, minuteAngle: 0, secondAngle: 0 });
    var elapsedEndTimeRef = useRef((_k = clockElapsedTimeDisplay === null || clockElapsedTimeDisplay === void 0 ? void 0 : clockElapsedTimeDisplay.endTime) !== null && _k !== void 0 ? _k : { hourAngle: 0, minuteAngle: 0, secondAngle: 0 });
    // Refs to track current time during run (to avoid stale closure issues)
    var currentHourRef = useRef(hour);
    var currentMinuteRef = useRef(minute);
    // Ref to track time when run starts (for undo/redo)
    var runStartTimeRef = useRef(null);
    // Ref to track elapsed time state at drag start (for undo/redo)
    var elapsedStartStateRef = useRef(null);
    // Ref to skip sync useEffects during local updates (prevents interference with local state)
    var isLocalElapsedUpdateRef = useRef(false);
    var _20 = useState({
        visible: false,
        hours: 0,
        x: 0,
        y: 0,
    }), rotationTooltip = _20[0], setRotationTooltip = _20[1];
    var _21 = useState(null), debugCenter = _21[0], setDebugCenter = _21[1];
    var _22 = useState(clockElapsedDragStarted !== null && clockElapsedDragStarted !== void 0 ? clockElapsedDragStarted : false), dragStartedState = _22[0], setDragStartedState = _22[1];
    var prevSelectedToolRef = useRef([]);
    var isFocused = useMemo(function () { return selectedTool.includes(id); }, [selectedTool, id]);
    var duplicateLabel = useDuplicateAnnounce(id, t('text.manipulativeClock'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeClock'));
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        if (nowFocused && !wasFocused && onWhiteBoard && wrapperRef.current) {
            setTimeout(function () {
                var _a, _b, _c;
                // Don't steal focus from the context menu
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_c = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('.dummy_btn')) === null || _c === void 0 ? void 0 : _c.focus();
            }, 100);
        }
    }, [selectedTool]);
    var _23 = useState(''), clockAnnouncement = _23[0], setClockAnnouncement = _23[1];
    var keydownStartRef = useRef(false);
    /** Returns an aria announcement string for the hour hand position in free-hand mode.
     *  Exact hours → "hour hand at 4"; between hours → "hour hand between 4 and 5". */
    var getFreeHandHourDesc = function (hourAngle) {
        var normalizedAngle = ((hourAngle % 360) + 360) % 360;
        var isExact = normalizedAngle % 30 === 0;
        var lowerHour = Math.floor(normalizedAngle / 30) || 12;
        if (isExact)
            return t('text.clockHandHourAt', { hour: lowerHour });
        var upperHour = lowerHour === 12 ? 1 : lowerHour + 1;
        return t('text.clockHandHourBetween', { from: lowerHour, to: upperHour });
    };
    var clockAriaLabel = useMemo(function () {
        var parts = [];
        parts.push(movement === 'geared' ? t('text.gearedClock') : t('text.freeMovingClock'));
        if (hourhandV)
            parts.push(movement === 'notgeared' ? getFreeHandHourDesc(_hourAngle) : t('text.clockHandHourAt', { hour: hour }));
        if (minutehandV)
            parts.push(t('text.clockHandMinuteAt', { minute: Math.round(minute < 1 ? 0 : minute) }));
        if (hourhandV && minutehandV) {
            var timeStr = "".concat(hour, ":").concat(String(Math.round(minute < 1 ? 0 : minute)).padStart(2, '0'));
            var showing = t('text.clockShowing', { time: timeStr });
            parts.push(movement === 'geared' ? "".concat(showing, " ").concat(currentPeriod) : showing);
        }
        if (clockDesign)
            parts.push(t("text.clockStyleName_".concat(clockDesign)));
        return parts.join(', ');
    }, [movement, hourhandV, minutehandV, hour, minute, currentPeriod, clockDesign, t]);
    useEffect(function () {
        setHourhandVisible(hourhandVisible);
        setMinutehandVisible(minutehandVisible);
        sethandLabelVisible(handLabelVisible);
    }, [hourhandVisible, minutehandVisible, handLabelVisible]);
    useEffect(function () {
        setonWhiteBoard(updateStyleOnwhiteboard);
    }, [updateStyleOnwhiteboard]);
    // Sync period ref and state when period prop changes
    useEffect(function () {
        periodRef.current = period;
        setCurrentPeriod(period);
    }, [period]);
    // Sync currentHourRef and currentMinuteRef when props change (e.g., from undo/redo)
    // Also detect external changes (undo) and reset RunJump timer
    useEffect(function () {
        // Check if time changed externally (refs don't match props)
        // During timeUpdate, refs are updated BEFORE setManipulative, so they match
        // During undo, refs are NOT updated beforehand, so they differ
        var refTime = currentHourRef.current * 60 + currentMinuteRef.current;
        var propTime = hour * 60 + minute;
        var isExternalChange = Math.abs(refTime - propTime) > 0.5; // Allow small tolerance for fractional minutes
        // Update refs
        currentHourRef.current = hour;
        currentMinuteRef.current = minute;
        // If external change (like undo/redo), reset RunJump timer
        if (isExternalChange && runJumpVisible && runJumpRef.current) {
            runJumpRef.current.handleRunStop();
        }
    }, [hour, minute, runJumpVisible]);
    // Sync second hand angle when runtimeSeconds changes in clockData (from undo/redo or pause/stop save)
    useEffect(function () {
        var seconds = clockRuntimeSeconds !== null && clockRuntimeSeconds !== void 0 ? clockRuntimeSeconds : 0;
        if (runJumpVisible && runJumpRef.current) {
            runJumpRef.current.setRuntimeSeconds(seconds);
        }
        // setSecondAngle((seconds % 60) * 6);
    }, [clockRuntimeSeconds]);
    // Sync prevHour ref when hour prop changes (for period crossing detection)
    useEffect(function () {
        prevHourForPeriodRef.current = hour;
    }, [hour]);
    // Sync digitalClockVisible state when clockData changes (e.g., after undo/redo)
    useEffect(function () {
        setDigitalClockVisible(clockDigitalClockVisible !== null && clockDigitalClockVisible !== void 0 ? clockDigitalClockVisible : false);
    }, [clockDigitalClockVisible]);
    // Sync shadingEnabled state when clockData changes (e.g., after undo/redo)
    useEffect(function () {
        setShadingEnabled(clockFractionOverlayShadingEnabled !== null && clockFractionOverlayShadingEnabled !== void 0 ? clockFractionOverlayShadingEnabled : true);
    }, [clockFractionOverlayShadingEnabled]);
    // Sync runJumpVisible state when clockData changes (e.g., after undo/redo)
    useEffect(function () {
        setRunJumpVisible(clockRunJumpVisible !== null && clockRunJumpVisible !== void 0 ? clockRunJumpVisible : false);
    }, [clockRunJumpVisible]);
    // Sync elapsedState when clockData changes (e.g., after undo/redo)
    useEffect(function () {
        setElapsedState(clockElapsedState !== null && clockElapsedState !== void 0 ? clockElapsedState : false);
    }, [clockElapsedState]);
    // Sync elapsedTimeActive when clockData changes (e.g., after undo/redo)
    // Skip during local updates to prevent interference
    useEffect(function () {
        if (isLocalElapsedUpdateRef.current)
            return;
        setElapsedTime(clockElapsedTimeActive !== null && clockElapsedTimeActive !== void 0 ? clockElapsedTimeActive : false);
    }, [clockElapsedTimeActive]);
    // Sync elapsedDragStarted when clockData changes (e.g., after undo/redo)
    // Skip during local updates to prevent interference
    useEffect(function () {
        if (isLocalElapsedUpdateRef.current)
            return;
        setDragStartedState(clockElapsedDragStarted !== null && clockElapsedDragStarted !== void 0 ? clockElapsedDragStarted : false);
    }, [clockElapsedDragStarted]);
    // Sync elapsedHours and elapsedReferenceAngle when clockData changes (e.g., after undo/redo)
    // Combined to ensure tooltip and angles are updated together
    // Skip during local updates to prevent interference
    useEffect(function () {
        if (isLocalElapsedUpdateRef.current) {
            isLocalElapsedUpdateRef.current = false; // Reset flag after skipping
            return;
        }
        var newElapsedHours = clockElapsedHoursCount !== null && clockElapsedHoursCount !== void 0 ? clockElapsedHoursCount : 0;
        var newReferenceAngle = clockElapsedReferenceAngle !== null && clockElapsedReferenceAngle !== void 0 ? clockElapsedReferenceAngle : null;
        var newPrevHourAngle = clockElapsedPrevHourAngle !== null && clockElapsedPrevHourAngle !== void 0 ? clockElapsedPrevHourAngle : null;
        var newTotalMinuteAngle = clockElapsedTotalMinuteAngle !== null && clockElapsedTotalMinuteAngle !== void 0 ? clockElapsedTotalMinuteAngle : null;
        setElapsedHours(newElapsedHours);
        elapsedHoursRef.current = newElapsedHours;
        elapsedFractionRef.current = clockElapsedFraction !== null && clockElapsedFraction !== void 0 ? clockElapsedFraction : 0;
        if (newReferenceAngle !== null && clockElapsedTimeActive) {
            elapsedReferenceMinuteAngleRef.current = newReferenceAngle;
            elapsedPrevMinuteAngleRef.current = newReferenceAngle;
            elapsedPrevHourAngleRef.current = newPrevHourAngle;
            // Only update clock hands and tooltip if drag has started (restoring after a drag)
            // When just activating elapsed time (dragStarted = false), don't change clock hands
            if (clockElapsedDragStarted && newTotalMinuteAngle !== null) {
                // Use the actual saved totalMinuteAngle for restoring clock position
                // This is more accurate than calculating from reference + hours * 360
                var restoredTotalAngle = newTotalMinuteAngle;
                totalMinuteAngleRef.current = restoredTotalAngle;
                setTotalMinuteAngle(restoredTotalAngle);
                // Also update the visual angles for the clock hands
                // Hour angle is calculated from TOTAL angle (not normalized) because
                // hour hand position depends on total rotation including full circles
                var normalizedMinuteAngle = ((restoredTotalAngle % 360) + 360) % 360;
                var hourAngleFromTotal = restoredTotalAngle / 12;
                setMinuteAngle(normalizedMinuteAngle);
                setHourAngle(hourAngleFromTotal);
                // Update tooltip position
                updateRotationTooltipPositionFromAngle(restoredTotalAngle, newElapsedHours, true);
            }
            else if (!clockElapsedDragStarted) {
                // Undo to pre-drag state: elapsed time is active but no drag has happened yet
                // Hide tooltip since we're back to the initial elapsed time state
                setRotationTooltip(function (prev) { return (__assign(__assign({}, prev), { visible: false, hours: 0 })); });
                setDragStartedState(false);
                // Also restore clock hands position to the initial state
                // Use the saved totalMinuteAngle if available, otherwise use reference angle
                var restoredTotalAngle = newTotalMinuteAngle !== null && newTotalMinuteAngle !== void 0 ? newTotalMinuteAngle : newReferenceAngle;
                if (restoredTotalAngle !== null) {
                    totalMinuteAngleRef.current = restoredTotalAngle;
                    setTotalMinuteAngle(restoredTotalAngle);
                    // Update visual angles
                    var normalizedMinuteAngle = ((restoredTotalAngle % 360) + 360) % 360;
                    var hourAngleFromTotal = restoredTotalAngle / 12;
                    setMinuteAngle(normalizedMinuteAngle);
                    setHourAngle(hourAngleFromTotal);
                }
            }
        }
        else if (newReferenceAngle === null) {
            elapsedReferenceMinuteAngleRef.current = null;
            elapsedPrevMinuteAngleRef.current = null;
            elapsedPrevHourAngleRef.current = null;
            // Hide tooltip when elapsed time tracking is reset
            setRotationTooltip(function (prev) { return (__assign(__assign({}, prev), { visible: false, hours: 0 })); });
        }
    }, [
        clockElapsedHoursCount,
        clockElapsedReferenceAngle,
        clockElapsedPrevHourAngle,
        clockElapsedTotalMinuteAngle,
        clockElapsedFraction,
        clockElapsedTimeActive,
        clockElapsedDragStarted,
    ]);
    // Sync fraction overlay values from clockData
    useEffect(function () {
        // The values are already read from _clockData, so we just need to ensure
        // the component re-renders when _clockData changes
    }, [_clockData.fractionOverlayParts, _clockData.fractionOverlayRotation]);
    // Sync notGeared refs when hour/minute props change (e.g., from digital clock input)
    // Skip if we just finished dragging to preserve the exact angle position
    useEffect(function () {
        if (isDraggingRef.current || isKeyboardActiveRef.current)
            return;
        if (justFinishedDraggingRef.current) {
            justFinishedDraggingRef.current = false;
            return;
        }
        if (movement === 'notgeared') {
            // Use stored angles from clockData if available, otherwise calculate from hour/minute
            notGearedHourAngleRef.current =
                _clockData.hourAngle !== undefined && _clockData.hourAngle !== 0
                    ? _clockData.hourAngle
                    : (hour === 12 ? 0 : hour) * 30;
            notGearedMinuteAngleRef.current =
                _clockData.minuteAngle !== undefined && _clockData.minuteAngle !== 0
                    ? _clockData.minuteAngle
                    : minute * 6;
        }
    }, [hour, minute, movement, _clockData.hourAngle, _clockData.minuteAngle]);
    // SYNC WITH DIGITAL CLOCK INPUT — derive angles from hour/minute
    useEffect(function () {
        if (isDraggingRef.current || isKeyboardActiveRef.current)
            return;
        if (justFinishedDraggingRef.current) {
            justFinishedDraggingRef.current = false;
            return;
        }
        // Don't reset if elapsed time is active and tracking
        // Use PROPS (clockElapsedTimeActive, clockElapsedDragStarted) instead of STATE
        // to ensure undo/redo works correctly (props reflect the restored values)
        if (clockElapsedTimeActive &&
            (clockElapsedDragStarted || secondsVisible || isInitializingElapsedRef.current)) {
            return;
        }
        // For notgeared mode, use stored angles from clockData/refs (hour not influenced by minute)
        // For geared mode, use computed angles which couple hour to minute
        var newHourAngle = movement === 'notgeared'
            ? _clockData.hourAngle !== undefined && _clockData.hourAngle !== 0
                ? _clockData.hourAngle
                : notGearedHourAngleRef.current
            : computedAngles.hourAngle;
        var newMinuteAngle = movement === 'notgeared'
            ? _clockData.minuteAngle !== undefined && _clockData.minuteAngle !== 0
                ? _clockData.minuteAngle
                : notGearedMinuteAngleRef.current
            : computedAngles.minuteAngle;
        notGearedHourAngleRef.current = newHourAngle;
        notGearedMinuteAngleRef.current = newMinuteAngle;
        setHourAngle(newHourAngle);
        setMinuteAngle(newMinuteAngle);
        setTotalMinuteAngle(newMinuteAngle);
        totalMinuteAngleRef.current = newMinuteAngle;
    }, [computedAngles.hourAngle, computedAngles.minuteAngle, movement, hour, minute]);
    useEffect(function () {
        if (movement === 'notgeared') {
            setRunJumpVisible(false);
            setDigitalClockVisible(false);
            setElapsedState(false);
            setElapsedTime(false);
            setDragStartedState(false);
            setElapsedHours(0);
            elapsedHoursRef.current = 0;
            elapsedFractionRef.current = 0;
            elapsedReferenceMinuteAngleRef.current = null;
            elapsedPrevMinuteAngleRef.current = null;
            elapsedPrevHourAngleRef.current = null;
            elapsedStartTimeRef.current = { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
            elapsedEndTimeRef.current = { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
            setRotationTooltip(function (prevTooltip) { return (__assign(__assign({}, prevTooltip), { visible: false, hours: 0 })); });
            isInitializingElapsedRef.current = false;
        }
    }, [movement]);
    useEffect(function () {
        elapsedHoursRef.current = elapsedHours;
    }, [elapsedHours]);
    var getClientPoint = function (e) {
        if ('touches' in e) {
            var touch = e.touches[0] || e.changedTouches[0];
            return { x: touch.clientX, y: touch.clientY };
        }
        return { x: e.clientX, y: e.clientY };
    };
    var updateRotationTooltipPositionFromAngle = function (angle, hours, visiblility, distance) {
        var _a, _b;
        if (distance === void 0) { distance = 20; }
        var rect = (_a = clockRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        var wrapperRect = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!rect || !wrapperRect)
            return;
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var radius = rect.width / (2 * zoomLevel * (scale !== null && scale !== void 0 ? scale : 1));
        var centerX = (cx - wrapperRect.left) / (zoomLevel * (scale !== null && scale !== void 0 ? scale : 1));
        var centerY = (cy - wrapperRect.top) / (zoomLevel * (scale !== null && scale !== void 0 ? scale : 1));
        var _result = pointFromCenterAndAngle(centerX, centerX, angle - 90, radius + 20);
        setDebugCenter({ x: centerX, y: centerY });
        setRotationTooltip(function (prev) { return (__assign(__assign({}, prev), { x: _result.x, y: _result.y, hours: hours, visible: visiblility })); });
    };
    var updateParent = useCallback(function (hAngle, mAngle) {
        var time = calculateTimeFromAngles(hAngle, mAngle);
        // Detect period (AM/PM) change when hour crosses 12
        var prevHour = prevHourForPeriodRef.current;
        var newHour = time.hour;
        var newPeriod = periodRef.current;
        // Crossing from 11 to 12 (forward) or 12 to 11 (backward) toggles period
        if ((prevHour === 11 && newHour === 12) || (prevHour === 12 && newHour === 11)) {
            newPeriod = periodRef.current === 'AM' ? 'PM' : 'AM';
            periodRef.current = newPeriod;
            // Update state for real-time DigitalClock update during drag
            setCurrentPeriod(newPeriod);
        }
        // Update prev hour ref for next comparison
        prevHourForPeriodRef.current = newHour;
        // Update elapsed endTime if elapsed drag is active
        if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
            elapsedEndTimeRef.current = {
                hourAngle: notGearedHourAngleRef.current,
                minuteAngle: notGearedMinuteAngleRef.current,
                secondAngle: _secondAngle,
            };
        }
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                if (_man.data.type === 'analog' && _man.data.id === id) {
                    return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { hour: time.hour, minute: time.minute, period: newPeriod, 
                            // Store the angles so they persist across mode switches and re-renders
                            hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current }) });
                }
                return _man;
            });
        });
    }, [id]);
    // Helper function to update fraction overlay parts in manipulative
    var updateFractionOverlayParts = useCallback(function (parts) {
        var _a, _b, _c, _d;
        // Find the current item to get old data for undo/redo
        var currentItem = manipulative.find(function (_man) { return _man.data.type === 'analog' && _man.data.id === id; });
        if (!currentItem)
            return;
        var currentData = currentItem.data;
        // Calculate new values
        var newRotation = parts ? (_a = currentData.fractionOverlayRotation) !== null && _a !== void 0 ? _a : 0 : 0;
        var newColors = parts
            ? currentData.fractionOverlayColors && currentData.fractionOverlayColors.length === parts
                ? currentData.fractionOverlayColors
                : Array(parts).fill('transparent')
            : undefined;
        // Save state for undo/redo with completeData (multiple properties changing)
        var oldData = {
            fractionOverlayParts: (_b = currentData.fractionOverlayParts) !== null && _b !== void 0 ? _b : null,
            fractionOverlayRotation: (_c = currentData.fractionOverlayRotation) !== null && _c !== void 0 ? _c : 0,
            fractionOverlayColors: (_d = currentData.fractionOverlayColors) !== null && _d !== void 0 ? _d : [],
        };
        var newData = {
            fractionOverlayParts: parts,
            fractionOverlayRotation: newRotation,
            fractionOverlayColors: newColors,
        };
        if (JSON.stringify(oldData) !== JSON.stringify(newData) && parts !== null) {
            saveState({
                type: id,
                id: id.substring(id.indexOf('_') + 1),
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
        }
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                return _man.data.type === 'analog' && _man.data.id === id
                    ? __assign(__assign({}, _man), { data: __assign(__assign(__assign(__assign({}, _man.data), { fractionOverlayParts: parts, fractionOverlayRotation: newRotation, fractionOverlayColors: newColors }), (parts !== null &&
                            currentData.fractionOverlayShadingEnabled === undefined && {
                            fractionOverlayShadingEnabled: true,
                        })), { fractionDisplay: {
                                angle: newRotation,
                                fragments: (newColors !== null && newColors !== void 0 ? newColors : []).map(function (c) { return ({ color: c }); }),
                                shown: parts !== null,
                            } }) }) : _man;
            });
        });
    }, [id, setManipulative, manipulative]);
    // Helper function to update fraction overlay rotation in manipulative
    var updateFractionOverlayRotation = useCallback(function (rotation) {
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                if (_man.data.type === 'analog' && _man.data.id === id) {
                    var data = _man.data;
                    return __assign(__assign({}, _man), { data: __assign(__assign({}, data), { fractionOverlayRotation: rotation, fractionDisplay: data.fractionDisplay
                                ? __assign(__assign({}, data.fractionDisplay), { angle: rotation }) : undefined }) });
                }
                return _man;
            });
        });
    }, [id, setManipulative]);
    // Helper function to update fraction overlay colors in manipulative
    var updateFractionOverlayColors = useCallback(function (colors) {
        // Save state for undo/redo
        var oldColors = fractionOverlayColors;
        if (JSON.stringify(oldColors) !== JSON.stringify(colors)) {
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'fractionOverlayColors',
                old: JSON.stringify(oldColors),
                new: JSON.stringify(colors),
            });
        }
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                if (_man.data.type === 'analog' && _man.data.id === id) {
                    var data = _man.data;
                    return __assign(__assign({}, _man), { data: __assign(__assign({}, data), { fractionOverlayColors: colors, fractionDisplay: data.fractionDisplay
                                ? __assign(__assign({}, data.fractionDisplay), { fragments: colors.map(function (c) { return ({ color: c }); }) }) : undefined }) });
                }
                return _man;
            });
        });
    }, [id, setManipulative, fractionOverlayColors]);
    /** --- KEYBOARD HAND NAVIGATION --- */
    var handleMinuteHandKeyDown = function (e) {
        var _a, _b, _c, _d;
        if (!onWhiteBoard || panningEnable || (lock === null || lock === void 0 ? void 0 : lock.positioning))
            return;
        if (e.key === 'Escape') {
            e.preventDefault();
            (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.dummy_btn')) === null || _b === void 0 ? void 0 : _b.focus();
            return;
        }
        var arrowKeys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'];
        if (!arrowKeys.includes(e.key))
            return;
        e.preventDefault();
        e.stopPropagation();
        // Auto-select if not already selected
        if (!selectedTool.includes(id))
            setSelectedTool([id]);
        isKeyboardActiveRef.current = true;
        if (!e.repeat) {
            if (runJumpVisible)
                (_c = runJumpRef.current) === null || _c === void 0 ? void 0 : _c.handleRunStop();
            if (elapsedState && !dragStartedState) {
                elapsedReferenceMinuteAngleRef.current = totalMinuteAngleRef.current;
                elapsedPrevMinuteAngleRef.current = totalMinuteAngleRef.current;
                elapsedPrevHourAngleRef.current = _hourAngle;
                setDragStartedState(true);
            }
        }
        var delta = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
            delta = e.shiftKey ? 30 : 6;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
            delta = e.shiftKey ? -30 : -6;
        var isElapsedActive = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
        var oldData = __assign({ hour: currentHourRef.current, minute: Math.round(currentMinuteRef.current), period: periodRef.current, hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current }, (isElapsedActive
            ? {
                elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                    dragStarted: dragStartedState,
                    hoursCount: elapsedHoursRef.current,
                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                    prevHourAngle: elapsedPrevHourAngleRef.current,
                    totalMinuteAngle: totalMinuteAngleRef.current,
                    fraction: elapsedFractionRef.current,
                }),
            }
            : {}));
        var newTime;
        if (movement === 'geared') {
            // On first keypress, sync totalMinuteAngleRef from current hour angle
            // to prevent hour hand from resetting (Effect C may have normalized it to 0-360)
            if (!e.repeat) {
                var expectedTotal = _hourAngle * 12;
                if (Math.abs(totalMinuteAngleRef.current - expectedTotal) > 1) {
                    totalMinuteAngleRef.current = expectedTotal;
                }
            }
            totalMinuteAngleRef.current += delta;
            setTotalMinuteAngle(totalMinuteAngleRef.current);
            setMinuteAngle(totalMinuteAngleRef.current);
            setHourAngle(totalMinuteAngleRef.current / 12);
            notGearedMinuteAngleRef.current = totalMinuteAngleRef.current;
            notGearedHourAngleRef.current = totalMinuteAngleRef.current / 12;
            // Elapsed time tracking (mirrors handleDragMove logic)
            if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                var referenceAngle = elapsedReferenceMinuteAngleRef.current;
                var prevAngle = (_d = elapsedPrevMinuteAngleRef.current) !== null && _d !== void 0 ? _d : totalMinuteAngleRef.current - delta;
                var newTotal = totalMinuteAngleRef.current;
                // Hour crossing detection
                var prevIndex = Math.trunc((prevAngle - referenceAngle) / 360);
                var currIndex = Math.trunc((newTotal - referenceAngle) / 360);
                if (currIndex !== prevIndex) {
                    elapsedHoursRef.current = currIndex;
                    setElapsedHours(currIndex);
                }
                // Update fraction
                elapsedFractionRef.current = computeElapsedFraction(newTotal, referenceAngle, elapsedHoursRef.current, elapsedFractionRef.current, delta);
                // Update prev angle ref
                elapsedPrevMinuteAngleRef.current = newTotal;
                // Update tooltip
                updateRotationTooltipPositionFromAngle(newTotal, elapsedHoursRef.current, true);
                // Flag as local update to prevent sync useEffect from resetting
                isLocalElapsedUpdateRef.current = true;
            }
            newTime = calculateTimeFromAngles(totalMinuteAngleRef.current / 12, totalMinuteAngleRef.current);
        }
        else {
            notGearedMinuteAngleRef.current += delta;
            setMinuteAngle(notGearedMinuteAngleRef.current);
            newTime = calculateTimeFromAngles(notGearedHourAngleRef.current, notGearedMinuteAngleRef.current);
        }
        var prevHour = prevHourForPeriodRef.current;
        var newHour = newTime.hour;
        var newPeriod = periodRef.current;
        if ((prevHour === 11 && newHour === 12) || (prevHour === 12 && newHour === 11)) {
            newPeriod = periodRef.current === 'AM' ? 'PM' : 'AM';
            periodRef.current = newPeriod;
            setCurrentPeriod(newPeriod);
        }
        prevHourForPeriodRef.current = newHour;
        var newData = __assign({ hour: newTime.hour, minute: newTime.minute, period: newPeriod, hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current }, (isElapsedActive
            ? {
                elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                    dragStarted: true,
                    hoursCount: elapsedHoursRef.current,
                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                    prevHourAngle: elapsedPrevHourAngleRef.current,
                    totalMinuteAngle: totalMinuteAngleRef.current,
                    fraction: elapsedFractionRef.current,
                }),
            }
            : {}));
        if (!e.repeat) {
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
        }
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                if (_man.data.type === 'analog' && _man.data.id === id) {
                    return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), newData), (isElapsedActive
                            ? {
                                elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                    dragStarted: true,
                                    hoursCount: elapsedHoursRef.current,
                                    totalMinuteAngle: totalMinuteAngleRef.current,
                                    fraction: elapsedFractionRef.current,
                                }),
                            }
                            : {})) });
                }
                return _man;
            });
        });
        var msg;
        if (movement === 'notgeared') {
            // Free-hand: announce actual hand positions (hour hand may be between hours)
            msg = "".concat(getFreeHandHourDesc(notGearedHourAngleRef.current), ", ").concat(t('text.clockHandMinuteAt', { minute: newTime.minute }));
        }
        else {
            var timeStr = "".concat(newTime.hour, ":").concat(String(newTime.minute).padStart(2, '0'));
            msg = t('text.clockTimeAnnouncement', { time: timeStr, period: newPeriod });
        }
        setClockAnnouncement('');
        requestAnimationFrame(function () { return setClockAnnouncement(msg); });
    };
    var handleHourHandKeyDown = function (e) {
        var _a, _b, _c, _d;
        if (!onWhiteBoard || panningEnable || (lock === null || lock === void 0 ? void 0 : lock.positioning))
            return;
        if (e.key === 'Escape') {
            e.preventDefault();
            (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.dummy_btn')) === null || _b === void 0 ? void 0 : _b.focus();
            return;
        }
        var arrowKeys = ['ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown'];
        if (!arrowKeys.includes(e.key))
            return;
        e.preventDefault();
        e.stopPropagation();
        // Auto-select if not already selected
        if (!selectedTool.includes(id))
            setSelectedTool([id]);
        isKeyboardActiveRef.current = true;
        if (!e.repeat) {
            if (runJumpVisible)
                (_c = runJumpRef.current) === null || _c === void 0 ? void 0 : _c.handleRunStop();
            if (elapsedState && !dragStartedState) {
                elapsedReferenceMinuteAngleRef.current = totalMinuteAngleRef.current;
                elapsedPrevMinuteAngleRef.current = totalMinuteAngleRef.current;
                elapsedPrevHourAngleRef.current = _hourAngle;
                setDragStartedState(true);
            }
        }
        var delta = 0;
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp')
            delta = 30;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown')
            delta = -30;
        var isElapsedActive = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
        var oldData = __assign({ hour: currentHourRef.current, minute: Math.round(currentMinuteRef.current), period: periodRef.current, hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current }, (isElapsedActive
            ? {
                elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                    dragStarted: dragStartedState,
                    hoursCount: elapsedHoursRef.current,
                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                    prevHourAngle: elapsedPrevHourAngleRef.current,
                    totalMinuteAngle: totalMinuteAngleRef.current,
                    fraction: elapsedFractionRef.current,
                }),
            }
            : {}));
        var newTime;
        if (movement === 'geared') {
            var minuteDelta = delta * 12;
            totalMinuteAngleRef.current += minuteDelta;
            setTotalMinuteAngle(totalMinuteAngleRef.current);
            setMinuteAngle(totalMinuteAngleRef.current);
            setHourAngle(totalMinuteAngleRef.current / 12);
            notGearedMinuteAngleRef.current = totalMinuteAngleRef.current;
            notGearedHourAngleRef.current = totalMinuteAngleRef.current / 12;
            // Elapsed time tracking (same as handleMinuteHandKeyDown)
            if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                var referenceAngle = elapsedReferenceMinuteAngleRef.current;
                var prevAngle = (_d = elapsedPrevMinuteAngleRef.current) !== null && _d !== void 0 ? _d : (totalMinuteAngleRef.current - minuteDelta);
                var newTotal = totalMinuteAngleRef.current;
                var prevIndex = Math.trunc((prevAngle - referenceAngle) / 360);
                var currIndex = Math.trunc((newTotal - referenceAngle) / 360);
                if (currIndex !== prevIndex) {
                    elapsedHoursRef.current = currIndex;
                    setElapsedHours(currIndex);
                }
                elapsedFractionRef.current = computeElapsedFraction(newTotal, referenceAngle, elapsedHoursRef.current, elapsedFractionRef.current, minuteDelta);
                elapsedPrevMinuteAngleRef.current = newTotal;
                updateRotationTooltipPositionFromAngle(newTotal, elapsedHoursRef.current, true);
                isLocalElapsedUpdateRef.current = true;
            }
            newTime = calculateTimeFromAngles(totalMinuteAngleRef.current / 12, totalMinuteAngleRef.current);
        }
        else {
            notGearedHourAngleRef.current += delta;
            setHourAngle(notGearedHourAngleRef.current);
            newTime = calculateTimeFromAngles(notGearedHourAngleRef.current, notGearedMinuteAngleRef.current);
        }
        var prevHour = prevHourForPeriodRef.current;
        var newHour = newTime.hour;
        var newPeriod = periodRef.current;
        if ((prevHour === 11 && newHour === 12) || (prevHour === 12 && newHour === 11)) {
            newPeriod = periodRef.current === 'AM' ? 'PM' : 'AM';
            periodRef.current = newPeriod;
            setCurrentPeriod(newPeriod);
        }
        prevHourForPeriodRef.current = newHour;
        var newData = __assign({ hour: newTime.hour, minute: newTime.minute, period: newPeriod, hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current }, (isElapsedActive
            ? {
                elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                    dragStarted: true,
                    hoursCount: elapsedHoursRef.current,
                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                    prevHourAngle: elapsedPrevHourAngleRef.current,
                    totalMinuteAngle: totalMinuteAngleRef.current,
                    fraction: elapsedFractionRef.current,
                }),
            }
            : {}));
        if (!e.repeat) {
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(newData),
            });
        }
        setManipulative(function (prev) {
            return prev.map(function (_man) {
                if (_man.data.type === 'analog' && _man.data.id === id) {
                    return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), newData), (isElapsedActive
                            ? {
                                elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                    dragStarted: true,
                                    hoursCount: elapsedHoursRef.current,
                                    totalMinuteAngle: totalMinuteAngleRef.current,
                                    fraction: elapsedFractionRef.current,
                                }),
                            }
                            : {})) });
                }
                return _man;
            });
        });
        var msg;
        if (movement === 'notgeared') {
            // Free-hand: announce actual hand positions (hour hand may be between hours)
            msg = "".concat(getFreeHandHourDesc(notGearedHourAngleRef.current), ", ").concat(t('text.clockHandMinuteAt', { minute: newTime.minute }));
        }
        else {
            var timeStr = "".concat(newTime.hour, ":").concat(String(newTime.minute).padStart(2, '0'));
            msg = t('text.clockTimeAnnouncement', { time: timeStr, period: newPeriod });
        }
        setClockAnnouncement('');
        requestAnimationFrame(function () { return setClockAnnouncement(msg); });
    };
    var handleHandKeyUp = function () {
        keydownStartRef.current = false;
        isKeyboardActiveRef.current = false;
        justFinishedDraggingRef.current = true; // Prevents Effect C from resetting on next render
    };
    /** --- MAIN ROTATION HANDLER --- */
    useEffect(function () {
        var handleDragMove = function (e) {
            var _a;
            if (!clockRef.current)
                return;
            if (!hourboolRef.current && !minuteboolRef.current)
                return;
            if ('touches' in e) {
                e.preventDefault();
            }
            var rect = clockRef.current.getBoundingClientRect();
            var cx = rect.left + rect.width / 2;
            var cy = rect.top + rect.height / 2;
            var point = getClientPoint(e);
            var dx = point.x - cx;
            var dy = point.y - cy;
            var rad = Math.atan2(dy, dx);
            var rawAngle = rad * (180 / Math.PI) + 90;
            /** ------------------ FREE MODE ------------------- **/
            if (movement === 'notgeared') {
                if (hourboolRef.current) {
                    setHourAngle(rawAngle);
                    hourAngleRef.current = rawAngle;
                    // Update ref with raw angle
                    notGearedHourAngleRef.current = rawAngle;
                }
                if (minuteboolRef.current) {
                    setMinuteAngle(rawAngle);
                    minuteAngleRef.current = rawAngle;
                    // Update ref with raw angle
                    notGearedMinuteAngleRef.current = rawAngle;
                }
                // update prevRawAngle for next drag movement (both ref and state)
                prevRawAngleRef.current = rawAngle;
                setPrevRawAngle(rawAngle);
                return; // IMPORTANT: stop here
            }
            /** ------------------ GEARED MODE ------------------- **/
            // Use ref for immediate value access (avoids stale closure on first move after startDrag)
            var delta = rawAngle - prevRawAngleRef.current;
            if (delta > 180)
                delta -= 360;
            if (delta < -180)
                delta += 360;
            // Update both ref (immediate) and state (for dependency tracking)
            prevRawAngleRef.current = rawAngle;
            setPrevRawAngle(rawAngle);
            // Use ref for immediate value (avoids stale closure issue on first move)
            var currentTotalMinuteAngle = totalMinuteAngleRef.current;
            var minuteAngleNext = minuteboolRef.current
                ? currentTotalMinuteAngle + delta
                : currentTotalMinuteAngle + delta * 12;
            var nextHours = elapsedHoursRef.current;
            if (movement === 'geared' && elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                var reference = elapsedReferenceMinuteAngleRef.current;
                var prevAngle = (_a = elapsedPrevMinuteAngleRef.current) !== null && _a !== void 0 ? _a : minuteAngleNext;
                // Use Math.trunc instead of Math.floor to prevent false crossings when
                // dragging slightly backward from the reference. Math.floor(-0.1) = -1,
                // but Math.trunc(-0.1) = 0, which correctly indicates no full rotation.
                var prevIndex = Math.trunc((prevAngle - reference) / 360);
                var currIndex = Math.trunc((minuteAngleNext - reference) / 360);
                var crossings = currIndex - prevIndex;
                if (crossings !== 0) {
                    nextHours = elapsedHoursRef.current + crossings;
                    // Safeguard: If we're still initializing and get negative hours, reset to 0
                    if (isInitializingElapsedRef.current && nextHours < 0) {
                        nextHours = 0;
                    }
                    elapsedHoursRef.current = nextHours;
                    setElapsedHours(nextHours);
                }
                // Update fractional elapsed value AFTER hours update so it sees the correct elapsedHours
                elapsedFractionRef.current = computeElapsedFraction(minuteAngleNext, reference, elapsedHoursRef.current, elapsedFractionRef.current);
                elapsedPrevMinuteAngleRef.current = minuteAngleNext;
                updateRotationTooltipPositionFromAngle(minuteAngleNext, elapsedHoursRef.current, true);
            }
            // Update both ref and state
            totalMinuteAngleRef.current = minuteAngleNext;
            setTotalMinuteAngle(minuteAngleNext);
            // Also update notGeared refs so switching modes preserves position
            notGearedHourAngleRef.current = minuteAngleNext / 12;
            notGearedMinuteAngleRef.current = minuteAngleNext;
            // setTotalMinuteAngle(prev => {
            //   let _updated = prev;
            //   if (minuteboolRef.current) _updated = prev + delta; // minute drives hour
            //   if (hourboolRef.current) _updated = prev + delta * 12; // hour drives minute
            //   totalMinuteAngleRef.current = _updated;
            //   return _updated;
            // });
        };
        var handleDragEnd = function () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (movement === 'notgeared') {
                // Set flag to prevent refs from being reset when parent updates
                justFinishedDraggingRef.current = true;
                if (hourboolRef.current && hourAngleRef.current !== null) {
                    notGearedMinuteAngleRef.current = _minuteAngle;
                    updateParent(hourAngleRef.current, _minuteAngle);
                }
                if (minuteboolRef.current && minuteAngleRef.current !== null) {
                    // Calculate time from angles to get the rounded values
                    var time = calculateTimeFromAngles(notGearedHourAngleRef.current, minuteAngleRef.current);
                    // Calculate snapped angle from the rounded minute
                    var snappedMinuteAngle = time.minute * 6;
                    // Update notGearedMinuteAngleRef with the snapped angle (based on rounded time)
                    notGearedMinuteAngleRef.current = snappedMinuteAngle;
                    updateParent(notGearedHourAngleRef.current, minuteAngleRef.current);
                }
                // Save state for undo/redo in notgeared mode (include angles)
                // Note: Elapsed time is disabled in notgeared mode
                if (dragStartTimeRef.current) {
                    var newTime = calculateTimeFromAngles(notGearedHourAngleRef.current, notGearedMinuteAngleRef.current);
                    var oldTimeData = {
                        hour: dragStartTimeRef.current.hour,
                        minute: dragStartTimeRef.current.minute,
                        period: dragStartTimeRef.current.period,
                        hourAngle: dragStartTimeRef.current.hourAngle,
                        minuteAngle: dragStartTimeRef.current.minuteAngle,
                    };
                    var newTimeData = {
                        hour: newTime.hour,
                        minute: newTime.minute,
                        period: periodRef.current,
                        hourAngle: notGearedHourAngleRef.current,
                        minuteAngle: notGearedMinuteAngleRef.current,
                    };
                    // Only save if time or angles actually changed
                    if (oldTimeData.hour !== newTimeData.hour ||
                        oldTimeData.minute !== newTimeData.minute ||
                        oldTimeData.period !== newTimeData.period ||
                        oldTimeData.hourAngle !== newTimeData.hourAngle ||
                        oldTimeData.minuteAngle !== newTimeData.minuteAngle) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify(oldTimeData),
                            new: JSON.stringify(newTimeData),
                        });
                    }
                    dragStartTimeRef.current = null;
                    elapsedStartStateRef.current = null;
                }
            }
            else {
                // GEARED MODE: Snap to nearest minute and reset seconds
                // Snap total minute angle directly to nearest minute (6° per minute).
                // Avoids decomposing into fullRotations + remainder, which loses a
                // rotation at boundaries (e.g. Math.floor(1079.8/360)=2 with
                // snappedMinuteAngle=0 gives 720 instead of 1080).
                var snappedTotalMinuteAngle = Math.round(totalMinuteAngleRef.current / 6) * 6;
                var snappedMinuteAngle = ((snappedTotalMinuteAngle % 360) + 360) % 360;
                var snappedTime = calculateTimeFromAngles(snappedTotalMinuteAngle / 12, snappedTotalMinuteAngle);
                var snappedHourAngle = (snappedTime.hour === 12 ? 0 : snappedTime.hour) * 30 + (snappedTime.minute / 60) * 30;
                // Update refs with snapped values
                totalMinuteAngleRef.current = snappedTotalMinuteAngle;
                setTotalMinuteAngle(snappedTotalMinuteAngle);
                setMinuteAngle(snappedMinuteAngle);
                setHourAngle(snappedHourAngle);
                setSecondAngle(0); // Reset seconds
                // Update parent with snapped angles
                updateParent(snappedHourAngle, snappedTotalMinuteAngle);
                // Recalculate elapsed state from the snapped angle so it matches the visual position.
                // Without this, elapsedFractionRef keeps the pre-snap value from the last drag move.
                if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                    var reference = elapsedReferenceMinuteAngleRef.current;
                    var snappedHours = Math.trunc((snappedTotalMinuteAngle - reference) / 360);
                    elapsedHoursRef.current = snappedHours;
                    setElapsedHours(snappedHours);
                    elapsedFractionRef.current = computeElapsedFraction(snappedTotalMinuteAngle, reference, snappedHours, elapsedFractionRef.current);
                    elapsedPrevMinuteAngleRef.current = snappedTotalMinuteAngle;
                }
                // Save state for undo/redo (include angles and elapsed time state)
                if (dragStartTimeRef.current) {
                    var newTime = calculateTimeFromAngles(snappedHourAngle, snappedTotalMinuteAngle);
                    // Build old data (time + elapsed if applicable)
                    var oldData = {
                        hour: dragStartTimeRef.current.hour,
                        minute: dragStartTimeRef.current.minute,
                        period: dragStartTimeRef.current.period,
                        hourAngle: dragStartTimeRef.current.hourAngle,
                        minuteAngle: dragStartTimeRef.current.minuteAngle,
                    };
                    // Build new data (time + elapsed if applicable)
                    var newData_1 = {
                        hour: newTime.hour,
                        minute: newTime.minute,
                        period: periodRef.current,
                        hourAngle: snappedHourAngle,
                        minuteAngle: snappedMinuteAngle,
                    };
                    // Include elapsed time state if elapsed time is active
                    if (elapsedTime && elapsedStartStateRef.current) {
                        oldData.elapsedTimeDisplay = elapsedStartStateRef.current;
                        newData_1.elapsedTimeDisplay = mergeElapsed(elapsedStartStateRef.current, {
                            dragStarted: true,
                            hoursCount: elapsedHoursRef.current,
                            referenceAngle: elapsedReferenceMinuteAngleRef.current,
                            prevHourAngle: elapsedPrevHourAngleRef.current,
                            totalMinuteAngle: totalMinuteAngleRef.current,
                            fraction: elapsedFractionRef.current,
                        });
                    }
                    // Check if anything changed
                    var timeChanged = oldData.hour !== newData_1.hour ||
                        oldData.minute !== newData_1.minute ||
                        oldData.period !== newData_1.period ||
                        oldData.hourAngle !== newData_1.hourAngle ||
                        oldData.minuteAngle !== newData_1.minuteAngle;
                    var elapsedChanged = elapsedTime &&
                        elapsedStartStateRef.current &&
                        (((_a = oldData.elapsedTimeDisplay) === null || _a === void 0 ? void 0 : _a.dragStarted) !== ((_b = newData_1.elapsedTimeDisplay) === null || _b === void 0 ? void 0 : _b.dragStarted) ||
                            ((_c = oldData.elapsedTimeDisplay) === null || _c === void 0 ? void 0 : _c.hoursCount) !== ((_d = newData_1.elapsedTimeDisplay) === null || _d === void 0 ? void 0 : _d.hoursCount) ||
                            ((_e = oldData.elapsedTimeDisplay) === null || _e === void 0 ? void 0 : _e.totalMinuteAngle) !== ((_f = newData_1.elapsedTimeDisplay) === null || _f === void 0 ? void 0 : _f.totalMinuteAngle));
                    if (timeChanged || elapsedChanged) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'completeData',
                            old: JSON.stringify(oldData),
                            new: JSON.stringify(newData_1),
                        });
                        // Update manipulative with elapsed time state if changed (set flag to skip sync useEffects)
                        if (elapsedChanged) {
                            // Track end time angles after drag
                            elapsedEndTimeRef.current = {
                                hourAngle: (_g = newData_1.hourAngle) !== null && _g !== void 0 ? _g : notGearedHourAngleRef.current,
                                minuteAngle: (_h = newData_1.minuteAngle) !== null && _h !== void 0 ? _h : notGearedMinuteAngleRef.current,
                                secondAngle: _secondAngle,
                            };
                            isLocalElapsedUpdateRef.current = true;
                            setManipulative(function (prev) {
                                return prev.map(function (_man) {
                                    if (_man.data.type === 'analog' && _man.data.id === id) {
                                        return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { elapsedTimeDisplay: mergeElapsed(newData_1.elapsedTimeDisplay, {
                                                    shown: true,
                                                    active: true,
                                                    startTime: elapsedStartTimeRef.current,
                                                    endTime: elapsedEndTimeRef.current,
                                                }) }) });
                                    }
                                    return _man;
                                });
                            });
                        }
                    }
                    dragStartTimeRef.current = null;
                    elapsedStartStateRef.current = null;
                }
            }
            hourboolRef.current = false;
            minuteboolRef.current = false;
            isDraggingRef.current = false;
            justFinishedDraggingRef.current = true;
            hourAngleRef.current = null;
            minuteAngleRef.current = null;
            // Update tooltip position with snapped angle if elapsed time is active
            if (movement === 'geared' && elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                updateRotationTooltipPositionFromAngle(totalMinuteAngleRef.current, elapsedHoursRef.current, true);
            }
            else {
                setRotationTooltip(function (prev) { return (__assign(__assign({}, prev), { visible: true })); });
            }
        };
        if (isDraggingRef.current && !panningEnable) {
            window.addEventListener('mousemove', handleDragMove);
            window.addEventListener('mouseup', handleDragEnd);
            window.addEventListener('touchmove', handleDragMove, { passive: false });
            window.addEventListener('touchend', handleDragEnd);
        }
        return function () {
            window.removeEventListener('mousemove', handleDragMove);
            window.removeEventListener('mouseup', handleDragEnd);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, [
        movement,
        prevRawAngle,
        _hourAngle,
        _minuteAngle,
        totalMinuteAngle,
        zoomLevel,
        dragStartedState,
        panningEnable,
        scale,
    ]);
    var startDrag = function (hand, e) {
        var _a;
        if (panningEnable) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!clockRef.current)
            return;
        if (!onWhiteBoard)
            return;
        if ('touches' in e) {
            e.preventDefault();
        }
        if (runJumpVisible) {
            // Stop the RunJump timer when dragging starts
            (_a = runJumpRef.current) === null || _a === void 0 ? void 0 : _a.handleRunStop();
        }
        isDraggingRef.current = true;
        var rect = clockRef.current.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var point = getClientPoint(e);
        var dx = point.x - cx;
        var dy = point.y - cy;
        var rad = Math.atan2(dy, dx);
        var rawAngle = rad * (180 / Math.PI) + 90;
        // IMPORTANT FIX: sync starting angle using ref for immediate access
        prevRawAngleRef.current = rawAngle;
        setPrevRawAngle(rawAngle);
        if (hand === 'hour') {
            hourboolRef.current = true;
        }
        else {
            hourboolRef.current = false;
        }
        minuteboolRef.current = !hourboolRef.current;
        hourAngleRef.current = _hourAngle;
        minuteAngleRef.current = _minuteAngle;
        isDraggingRef.current = true;
        // Initialize period refs for AM/PM tracking during drag
        periodRef.current = period;
        prevHourForPeriodRef.current = hour;
        // Capture initial time for undo/redo (include angles for notgeared mode)
        dragStartTimeRef.current = {
            hour: hour,
            minute: minute,
            period: period,
            hourAngle: notGearedHourAngleRef.current,
            minuteAngle: notGearedMinuteAngleRef.current,
        };
        // Capture initial elapsed time state for undo/redo (before dragStartedState changes)
        elapsedStartStateRef.current = mergeElapsed(clockElapsedTimeDisplay, {
            dragStarted: dragStartedState,
            hoursCount: elapsedHoursRef.current,
            referenceAngle: elapsedReferenceMinuteAngleRef.current,
            prevHourAngle: elapsedPrevHourAngleRef.current,
            totalMinuteAngle: totalMinuteAngleRef.current,
            fraction: elapsedFractionRef.current,
        });
        // In geared mode, initialize totalMinuteAngle from hour angle to preserve current position
        if (movement === 'geared') {
            var initialTotalMinuteAngle = _hourAngle * 12;
            // When elapsed time is active, compute from reference + elapsed fraction
            // instead of _hourAngle * 12 (which is normalized and loses accumulated rotations).
            // Without this, the crossing detection sees a huge offset from the reference
            // and triggers false crossings on tiny drags.
            if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                initialTotalMinuteAngle =
                    elapsedReferenceMinuteAngleRef.current + elapsedFractionRef.current * 360;
            }
            setTotalMinuteAngle(initialTotalMinuteAngle);
            totalMinuteAngleRef.current = initialTotalMinuteAngle; // Sync update ref immediately
            // CRITICAL: Sync elapsedPrevMinuteAngleRef to prevent false crossing detection
            // on the first drag move. Without this, the old prevAngle from a previous drag
            // could cause incorrect hour calculations when starting a new drag.
            if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                elapsedPrevMinuteAngleRef.current = initialTotalMinuteAngle;
                // Update tooltip position with accumulated angle (not normalized _minuteAngle)
                updateRotationTooltipPositionFromAngle(initialTotalMinuteAngle, elapsedHoursRef.current, true);
            }
        }
        setDragStartedState(true);
    };
    /** --- UPDATE DISPLAY ANGLES FROM MASTER ANGLE --- */
    useEffect(function () {
        if (!isDraggingRef.current)
            return;
        // In notgeared mode, each hand moves independently - angles are already set in handleDragMove
        // so we don't need to update them here based on totalMinuteAngle
        if (movement === 'notgeared') {
            // Do nothing - hour and minute hands are updated independently in handleDragMove
            return;
        }
        else {
            // In geared mode, both hands move together based on totalMinuteAngle
            setMinuteAngle(totalMinuteAngle);
            setHourAngle(totalMinuteAngle / 12);
            if (isDraggingRef.current) {
                minuteAngleRef.current = totalMinuteAngle;
                hourAngleRef.current = totalMinuteAngle / 12;
                // Also update notGeared refs so switching modes preserves position
                notGearedHourAngleRef.current = totalMinuteAngle / 12;
                notGearedMinuteAngleRef.current = totalMinuteAngle;
                // Update period during drag for real-time DigitalClock display
                var time = calculateTimeFromAngles(totalMinuteAngle / 12, totalMinuteAngle);
                var prevHour = prevHourForPeriodRef.current;
                var newHour = time.hour;
                // Crossing from 11 to 12 (forward) or 12 to 11 (backward) toggles period
                if ((prevHour === 11 && newHour === 12) || (prevHour === 12 && newHour === 11)) {
                    var newPeriod = periodRef.current === 'AM' ? 'PM' : 'AM';
                    periodRef.current = newPeriod;
                    setCurrentPeriod(newPeriod);
                }
                prevHourForPeriodRef.current = newHour;
            }
        }
    }, [totalMinuteAngle, movement]);
    /** Bottom Panel */
    useEffect(function () {
        var onToolClick = function (data) {
            var _a;
            // Geared-only tools: show alert popup instead of performing action on non-geared clocks
            var gearedOnlyTools = ['digitalDisplay', 'runAndJump', 'elapsedTime'];
            if (gearedOnlyTools.includes(data.id) && movement === 'notgeared') {
                setActiveManipulator('clockToolAlert');
                setManiplulatorComponentVisible(true);
                return;
            }
            switch (data.id) {
                case 'edit':
                    setActiveManipulator('clocks');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'scaleMinus':
                    var oldScaleMinus = scale !== null && scale !== void 0 ? scale : 1;
                    var newScaleMinus_1 = Math.max(0.5, oldScaleMinus - 0.1);
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(oldScaleMinus),
                        new: JSON.stringify(newScaleMinus_1),
                    });
                    setManipulative(function (prevMan) {
                        return prevMan.map(function (_man) {
                            return _man.data.type === 'analog' && _man.data.id === id
                                ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { scale: newScaleMinus_1 }) }) : _man;
                        });
                    });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.manipulativeClock'),
                        direction: t('text.down'),
                        value: newScaleMinus_1.toFixed(1),
                    }));
                    break;
                case 'scalePlus':
                    var oldScalePlus = scale !== null && scale !== void 0 ? scale : 1;
                    var newScalePlus_1 = oldScalePlus + 0.1;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'scale',
                        old: JSON.stringify(oldScalePlus),
                        new: JSON.stringify(newScalePlus_1),
                    });
                    setManipulative(function (prevMan) {
                        return prevMan.map(function (_man) {
                            return _man.data.type === 'analog' && _man.data.id === id
                                ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { scale: newScalePlus_1 }) }) : _man;
                        });
                    });
                    announce(t('text.manipulativeScaled', {
                        name: t('text.manipulativeClock'),
                        direction: t('text.up'),
                        value: newScalePlus_1.toFixed(1),
                    }));
                    break;
                case 'digitalDisplay':
                    var oldDigitalVisible = digitalClockVisible;
                    var newDigitalVisible_1 = !oldDigitalVisible;
                    // Save state for undo/redo
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'digitalClockVisible',
                        old: JSON.stringify(oldDigitalVisible),
                        new: JSON.stringify(newDigitalVisible_1),
                    });
                    // Update local state
                    setDigitalClockVisible(newDigitalVisible_1);
                    // Update manipulative data
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.type === 'analog' && _man.data.id === id
                                ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { digitalClockVisible: newDigitalVisible_1 }) }) : _man;
                        });
                    });
                    if (DISABLE_RUN_JUMP_AND_DIGITAL_DISPLAY_ON_ELAPSED) {
                        setElapsedState(false);
                        setDragStartedState(false);
                        setElapsedTime(false);
                        setElapsedHours(0);
                        elapsedHoursRef.current = 0;
                        elapsedFractionRef.current = 0;
                        elapsedReferenceMinuteAngleRef.current = null;
                        elapsedPrevMinuteAngleRef.current = null;
                        elapsedPrevHourAngleRef.current = null;
                        setRotationTooltip(function (prevTooltip) { return (__assign(__assign({}, prevTooltip), { visible: false, hours: 0 })); });
                        isInitializingElapsedRef.current = false;
                    }
                    announce(newDigitalVisible_1 ? t('text.digitalDisplayOn') : t('text.digitalDisplayOff'));
                    break;
                case 'runAndJump':
                    var oldRunJumpVisible = runJumpVisible;
                    var newRunJumpVisible_1 = !oldRunJumpVisible;
                    // Save state for undo/redo
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'runJumpVisible',
                        old: JSON.stringify(oldRunJumpVisible),
                        new: JSON.stringify(newRunJumpVisible_1),
                    });
                    // Update local state
                    setRunJumpVisible(newRunJumpVisible_1);
                    // Update manipulative data
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.type === 'analog' && _man.data.id === id
                                ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { runJumpVisible: newRunJumpVisible_1 }) }) : _man;
                        });
                    });
                    if (DISABLE_RUN_JUMP_AND_DIGITAL_DISPLAY_ON_ELAPSED) {
                        setElapsedState(false);
                        setDragStartedState(false);
                        setElapsedTime(false);
                        setElapsedHours(0);
                        elapsedHoursRef.current = 0;
                        elapsedFractionRef.current = 0;
                        elapsedReferenceMinuteAngleRef.current = null;
                        elapsedPrevMinuteAngleRef.current = null;
                        elapsedPrevHourAngleRef.current = null;
                        setRotationTooltip(function (prevTooltip) { return (__assign(__assign({}, prevTooltip), { visible: false, hours: 0 })); });
                        isInitializingElapsedRef.current = false;
                    }
                    announce(newRunJumpVisible_1 ? t('text.runAndJumpOn') : t('text.runAndJumpOff'));
                    break;
                case 'runPlay':
                    if (runJumpVisible && runJumpRef.current) {
                        var wasRunning_1 = runJumpRef.current.getIsRunning();
                        runJumpRef.current.handleRunPlay();
                        // Store running state so context menu can toggle label
                        setManipulative(function (prev) {
                            return prev.map(function (_man) {
                                return _man.data.type === 'analog' && _man.data.id === id
                                    ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { clockRunIsRunning: !wasRunning_1 }) }) : _man;
                            });
                        });
                    }
                    break;
                case 'runStop':
                    if (runJumpVisible && runJumpRef.current) {
                        runJumpRef.current.handleRunStop();
                        setManipulative(function (prev) {
                            return prev.map(function (_man) {
                                return _man.data.type === 'analog' && _man.data.id === id
                                    ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { clockRunIsRunning: false }) }) : _man;
                            });
                        });
                    }
                    break;
                case 'elapsedTime':
                    var newElapsedState_1 = !elapsedState;
                    // Save undo/redo state — snapshot all elapsed fields so undo restores full state
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify({
                            elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, { shown: !newElapsedState_1 }),
                        }),
                        new: JSON.stringify({
                            elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                                shown: newElapsedState_1,
                                active: false,
                                dragStarted: false,
                                hoursCount: 0,
                                referenceAngle: null,
                                prevHourAngle: null,
                                totalMinuteAngle: null,
                                fraction: 0,
                            }),
                        }),
                    });
                    // Capture start time when elapsed is toggled on; reset both when off
                    if (newElapsedState_1) {
                        elapsedStartTimeRef.current = {
                            hourAngle: _hourAngle,
                            minuteAngle: _minuteAngle,
                            secondAngle: _secondAngle,
                        };
                        elapsedEndTimeRef.current = {
                            hourAngle: _hourAngle,
                            minuteAngle: _minuteAngle,
                            secondAngle: _secondAngle,
                        };
                    }
                    else {
                        elapsedStartTimeRef.current = { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
                        elapsedEndTimeRef.current = { hourAngle: 0, minuteAngle: 0, secondAngle: 0 };
                    }
                    // Update manipulative state
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            if (_man.data.type === 'analog' && _man.data.id === id) {
                                return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                            shown: newElapsedState_1,
                                            active: false,
                                            startTime: elapsedStartTimeRef.current,
                                            endTime: elapsedEndTimeRef.current,
                                        }) }) });
                            }
                            return _man;
                        });
                    });
                    if (DISABLE_RUN_JUMP_AND_DIGITAL_DISPLAY_ON_ELAPSED) {
                        setDigitalClockVisible(false);
                        setRunJumpVisible(false);
                    }
                    setDragStartedState(false);
                    setElapsedState(newElapsedState_1);
                    setElapsedTime(false);
                    setElapsedHours(0);
                    elapsedHoursRef.current = 0;
                    elapsedFractionRef.current = 0;
                    elapsedReferenceMinuteAngleRef.current = null;
                    elapsedPrevMinuteAngleRef.current = null;
                    elapsedPrevHourAngleRef.current = null;
                    setRotationTooltip(function (prevTooltip) { return (__assign(__assign({}, prevTooltip), { visible: false, hours: 0 })); });
                    isInitializingElapsedRef.current = false;
                    announce(newElapsedState_1 ? t('text.elapsedTimeOn') : t('text.elapsedTimeOff'));
                    // Focus the elapsed time button when showing
                    if (newElapsedState_1) {
                        setTimeout(function () {
                            var wrapper = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                            var elapsedBtn = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.elapsedClickBtn');
                            elapsedBtn === null || elapsedBtn === void 0 ? void 0 : elapsedBtn.focus();
                        }, 150);
                    }
                    break;
                case 'fractionOverlay':
                    var nextPart = Number((_a = btnConfig.fractionOverlay.elements) === null || _a === void 0 ? void 0 : _a[data.data || 0].id);
                    var removed = nextPart === fractionOverlayParts;
                    updateFractionOverlayParts(removed ? null : nextPart);
                    announce(removed
                        ? t('text.fractionOverlayRemoved')
                        : t('text.fractionOverlaySet', { parts: nextPart }));
                    // Focus fraction overlay in section mode after adding
                    if (!removed) {
                        setTimeout(function () {
                            var wrapper = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                            var overlayEl = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.fraction-overlay');
                            if (overlayEl) {
                                overlayEl.focus();
                                setSectionMode(true);
                                setFocusedSectionIndex(0);
                            }
                        }, 200);
                    }
                    break;
                case 'fractionOverlay_collapsibleBtn':
                    // updateFractionOverlayParts(null);
                    break;
                case 'fractionOverlayShading': {
                    // Multi-select pattern: only first selected clock processes
                    var selectedClocks_1 = selectedTool.filter(function (sid) { return sid.startsWith('analog_'); });
                    if (id !== selectedClocks_1[0])
                        break;
                    // Aggregate: if any selected clock has shading OFF → turn all ON
                    var anyOff = selectedClocks_1.some(function (sid) {
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        return m && m.data.fractionOverlayShadingEnabled !== true;
                    });
                    var newShadingValue_1 = anyOff; // if any OFF → ON; if all ON → OFF
                    // Batch saveState for grouped undo
                    var shadingChanges_1 = [];
                    selectedClocks_1.forEach(function (sid) {
                        var _a;
                        var m = manipulative.find(function (item) { return item.data.id === sid; });
                        if (m) {
                            shadingChanges_1.push({
                                type: m.data.id,
                                id: m.data.id.substring(m.data.id.indexOf('_') + 1),
                                propertyName: 'fractionOverlayShadingEnabled',
                                old: JSON.stringify((_a = m.data.fractionOverlayShadingEnabled) !== null && _a !== void 0 ? _a : false),
                                new: JSON.stringify(newShadingValue_1),
                            });
                        }
                    });
                    saveState(shadingChanges_1);
                    // Update local state
                    setShadingEnabled(newShadingValue_1);
                    // Update all selected clocks
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            if (!selectedClocks_1.includes(_man.data.id))
                                return _man;
                            var data = _man.data;
                            return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, data), { fractionOverlayShadingEnabled: newShadingValue_1 }), (data.fractionDisplay && {
                                    fractionDisplay: __assign(__assign({}, data.fractionDisplay), { shown: newShadingValue_1 }),
                                })) });
                        });
                    });
                    announce(newShadingValue_1 ? t('text.fractionShadingOn') : t('text.fractionShadingOff'));
                    break;
                }
                // ── Update Time actions (context menu — works for both geared and not-geared) ──
                case 'clockIncreaseHour':
                case 'clockDecreaseHour':
                case 'clockIncreaseMinute':
                case 'clockDecreaseMinute':
                case 'clockTogglePeriod':
                case 'clockToggleFormat': {
                    var newHour = hour;
                    var newMinute = minute;
                    var newPeriod = period;
                    var newTimeFormat = _clockData.timeFormat || '12h';
                    switch (data.id) {
                        case 'clockIncreaseHour':
                            newHour = hour === 12 ? 1 : hour + 1;
                            if (hour === 11)
                                newPeriod = period === 'AM' ? 'PM' : 'AM';
                            break;
                        case 'clockDecreaseHour':
                            newHour = hour === 1 ? 12 : hour - 1;
                            if (hour === 12)
                                newPeriod = period === 'AM' ? 'PM' : 'AM';
                            break;
                        case 'clockIncreaseMinute':
                            newMinute = (minute + 1) % 60;
                            if (movement === 'geared' && minute === 59) {
                                newHour = hour === 12 ? 1 : hour + 1;
                                if (hour === 11)
                                    newPeriod = period === 'AM' ? 'PM' : 'AM';
                            }
                            break;
                        case 'clockDecreaseMinute':
                            newMinute = (minute - 1 + 60) % 60;
                            if (movement === 'geared' && minute === 0) {
                                newHour = hour === 1 ? 12 : hour - 1;
                                if (hour === 12)
                                    newPeriod = period === 'AM' ? 'PM' : 'AM';
                            }
                            break;
                        case 'clockTogglePeriod':
                            newPeriod = period === 'AM' ? 'PM' : 'AM';
                            break;
                        case 'clockToggleFormat':
                            newTimeFormat = newTimeFormat === '12h' ? '24h' : '12h';
                            break;
                    }
                    var newAngles = calculateAnglesFromTime(newHour, newMinute);
                    // For not-geared: keep current hour angle when only minutes change
                    var isMinuteOnlyChange = data.id === 'clockIncreaseMinute' || data.id === 'clockDecreaseMinute';
                    var newHourAngle = movement === 'geared'
                        ? newAngles.hourAngle
                        : isMinuteOnlyChange
                            ? notGearedHourAngleRef.current // Keep hour hand where it is (use ref — always current, avoids stale closure)
                            : (newHour === 12 ? 0 : newHour) * 30;
                    var oldTimeData = {
                        hour: hour,
                        minute: minute,
                        period: period,
                        hourAngle: movement === 'notgeared' ? notGearedHourAngleRef.current : _hourAngle,
                        minuteAngle: movement === 'notgeared' ? notGearedMinuteAngleRef.current : _minuteAngle,
                        timeFormat: _clockData.timeFormat,
                    };
                    var newTimeData_1 = {
                        hour: newHour,
                        minute: newMinute,
                        period: newPeriod,
                        hourAngle: newHourAngle,
                        minuteAngle: newAngles.minuteAngle,
                        timeFormat: newTimeFormat,
                    };
                    // When elapsed time is active, update tracking state and visual angles directly.
                    // The main sync useEffect is gated when clockElapsedDragStarted=true, so we must
                    // set angles explicitly — same pattern as the timeUpdate (RunJump) handler.
                    var isElapsedTracking = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                    if (isElapsedTracking) {
                        var oldHour24 = period === 'AM' ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;
                        var newHour24 = newPeriod === 'AM' ? (newHour === 12 ? 0 : newHour) : newHour === 12 ? 12 : newHour + 12;
                        var oldTotalMinutes = oldHour24 * 60 + minute;
                        var newTotalMinutes = newHour24 * 60 + newMinute;
                        var minuteDiff = newTotalMinutes - oldTotalMinutes;
                        if (minuteDiff > 720)
                            minuteDiff -= 1440;
                        if (minuteDiff < -720)
                            minuteDiff += 1440;
                        var angleDiff = minuteDiff * 6;
                        var newTotalMinuteAngle = totalMinuteAngleRef.current + angleDiff;
                        // Calculate elapsed hours crossing
                        var referenceAngle = elapsedReferenceMinuteAngleRef.current;
                        var prevTotalMinuteAngle = totalMinuteAngleRef.current;
                        var prevElapsedFraction = elapsedFractionRef.current;
                        var oldAngleFromRef = prevTotalMinuteAngle - referenceAngle;
                        var newAngleFromRef = newTotalMinuteAngle - referenceAngle;
                        var oldElapsedHoursSnapshot = elapsedHoursRef.current;
                        var newElapsedHours = Math.trunc(newAngleFromRef / 360);
                        if (newElapsedHours !== Math.trunc(oldAngleFromRef / 360)) {
                            elapsedHoursRef.current = newElapsedHours;
                            setElapsedHours(newElapsedHours);
                        }
                        elapsedFractionRef.current = computeElapsedFraction(newTotalMinuteAngle, referenceAngle, elapsedHoursRef.current, elapsedFractionRef.current, angleDiff);
                        // Update refs and visual angles
                        totalMinuteAngleRef.current = newTotalMinuteAngle;
                        setTotalMinuteAngle(newTotalMinuteAngle);
                        elapsedPrevMinuteAngleRef.current = newTotalMinuteAngle;
                        var normalizedMinuteAngle = ((newTotalMinuteAngle % 360) + 360) % 360;
                        var hourAngleFromTotal = newTotalMinuteAngle / 12;
                        setMinuteAngle(normalizedMinuteAngle);
                        setHourAngle(hourAngleFromTotal);
                        updateRotationTooltipPositionFromAngle(newTotalMinuteAngle, elapsedHoursRef.current, true);
                        // Start shading if not already started (same as onClockDataChange)
                        if (!dragStartedState) {
                            setDragStartedState(true);
                        }
                        // Include elapsed data in the store update and prevent sync effect interference
                        newTimeData_1.elapsedDragStarted = true;
                        newTimeData_1.elapsedReferenceAngle = referenceAngle;
                        newTimeData_1.elapsedTotalMinuteAngle = newTotalMinuteAngle;
                        newTimeData_1.elapsedHoursCount = elapsedHoursRef.current;
                        newTimeData_1.elapsedFraction = elapsedFractionRef.current;
                        oldTimeData.elapsedDragStarted = dragStartedState;
                        oldTimeData.elapsedReferenceAngle = referenceAngle;
                        oldTimeData.elapsedTotalMinuteAngle = prevTotalMinuteAngle;
                        oldTimeData.elapsedHoursCount = oldElapsedHoursSnapshot;
                        oldTimeData.elapsedFraction = prevElapsedFraction;
                        isLocalElapsedUpdateRef.current = true;
                    }
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldTimeData),
                        new: JSON.stringify(newTimeData_1),
                    });
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            return _man.data.type === 'analog' && _man.data.id === id
                                ? __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), newTimeData_1) }) : _man;
                        });
                    });
                    if (movement === 'notgeared') {
                        // Free-hand mode: describe hand positions using actual angles (hands move independently)
                        // Derive hour hand description from the real angle after the update
                        var hourAngleForAnnounce = isMinuteOnlyChange ? notGearedHourAngleRef.current : newHourAngle;
                        announce("".concat(getFreeHandHourDesc(hourAngleForAnnounce), ", ").concat(t('text.clockHandMinuteAt', { minute: newMinute })));
                    }
                    else {
                        var timeStr = "".concat(newHour, ":").concat(String(newMinute).padStart(2, '0'), " ").concat(newTimeFormat === '12h' ? newPeriod : '');
                        announce(t('text.timeSetTo', { time: timeStr.trim() }));
                    }
                    break;
                }
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
        }
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [
        isFocused,
        updateFractionOverlayParts,
        elapsedState,
        scaling,
        scale,
        JSON.stringify(manipulative),
        id,
        fractionOverlayParts,
        digitalClockVisible,
        runJumpVisible,
        shadingEnabled,
        selectedTool,
    ]);
    var _24 = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _24.startMultiDrag, moveMultiDrag = _24.moveMultiDrag, endMultiDrag = _24.endMultiDrag;
    /** Moving on whiteboard */
    var onPositionDragStart = function (e) {
        var _a, _b;
        if ((lock === null || lock === void 0 ? void 0 : lock.positioning) || panningEnable) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        positionDragRef.current = {
            startPos: { x: x, y: y },
            offset: { x: center.x - mouseXInParent, y: center.y - mouseYInParent },
            hasMoved: false,
        };
        // Capture initial position for undo/redo
        dragStartPositionRef.current = { x: center.x, y: center.y };
        setIsDraggingSelf(true);
        window.addEventListener('mousemove', onPositionDragMove);
        window.addEventListener('touchmove', onPositionDragMove, { passive: false });
        window.addEventListener('mouseup', onPositionDragEnd);
        window.addEventListener('touchend', onPositionDragEnd);
        startMultiDrag();
    };
    var getAngleFromCenter = function (clientX, clientY) {
        var _a;
        var overlayEl = (_a = fractionOverlayRef.current) !== null && _a !== void 0 ? _a : wrapperRef.current;
        if (!overlayEl)
            return null;
        var rect = overlayEl.getBoundingClientRect();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = clientX - cx;
        var dy = clientY - cy;
        var rad = Math.atan2(dy, dx);
        var angleDeg = (rad * 180) / Math.PI;
        return angleDeg;
    };
    // Rotation handle handlers (pattern block style)
    var onRotateHandleDown = function (e) {
        if (panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        var _a = getClientPoint(e), x = _a.x, y = _a.y;
        var startAngle = getAngleFromCenter(x, y);
        if (startAngle == null)
            return;
        rotatingRef.current = true;
        rotateStartAngleRef.current = startAngle;
        rotateInitialRotationRef.current = fractionOverlayRotation !== null && fractionOverlayRotation !== void 0 ? fractionOverlayRotation : 0;
        fractionRotationStartRef.current = fractionOverlayRotation !== null && fractionOverlayRotation !== void 0 ? fractionOverlayRotation : 0;
        window.addEventListener('mousemove', onRotateHandleMove);
        window.addEventListener('touchmove', onRotateHandleMove, { passive: false });
        window.addEventListener('mouseup', onRotateHandleEnd);
        window.addEventListener('touchend', onRotateHandleEnd);
    };
    var onRotateHandleMove = function (e) {
        if (!rotatingRef.current)
            return;
        var point = 'touches' in e
            ? { x: e.touches[0].clientX, y: e.touches[0].clientY }
            : { x: e.clientX, y: e.clientY };
        var currentAngle = getAngleFromCenter(point.x, point.y);
        if (currentAngle == null)
            return;
        var delta = currentAngle - rotateStartAngleRef.current;
        if (delta > 180)
            delta -= 360;
        if (delta < -180)
            delta += 360;
        var newRotation = rotateInitialRotationRef.current + delta;
        currentFractionRotationRef.current = newRotation;
        updateFractionOverlayRotation(newRotation);
    };
    var onRotateHandleEnd = function () {
        var _a;
        rotatingRef.current = false;
        if (fractionRotationStartRef.current !== null) {
            var oldRotation = fractionRotationStartRef.current;
            var newRotation = (_a = currentFractionRotationRef.current) !== null && _a !== void 0 ? _a : fractionOverlayRotation;
            if (oldRotation !== newRotation) {
                saveState({
                    type: id,
                    id: id.substring(id.indexOf('_') + 1),
                    propertyName: 'fractionOverlayRotation',
                    old: JSON.stringify(oldRotation),
                    new: JSON.stringify(newRotation),
                });
            }
        }
        fractionRotationStartRef.current = null;
        currentFractionRotationRef.current = null;
        window.removeEventListener('mousemove', onRotateHandleMove);
        window.removeEventListener('touchmove', onRotateHandleMove);
        window.removeEventListener('mouseup', onRotateHandleEnd);
        window.removeEventListener('touchend', onRotateHandleEnd);
    };
    var onPositionDragMove = function (e) {
        var _a, _b;
        if (!positionDragRef.current)
            return;
        if ('touches' in e)
            e.preventDefault();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var _d = positionDragRef.current, startPos = _d.startPos, offset = _d.offset, hasMoved = _d.hasMoved;
        if (!hasMoved) {
            var dx = x - startPos.x;
            var dy = y - startPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                positionDragRef.current.hasMoved = true;
            }
            else {
                return;
            }
        }
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        var newX = mouseXInParent + offset.x;
        var newY = mouseYInParent + offset.y;
        // Track current position in ref for undo/redo (avoids stale closure in onPositionDragEnd)
        currentPositionRef.current = { x: newX, y: newY };
        if (dragStartPositionRef.current) {
            moveMultiDrag(newX - dragStartPositionRef.current.x, newY - dragStartPositionRef.current.y);
        }
        // Throttle context update to once per animation frame to avoid re-rendering all widgets
        if (positionDragRafRef.current)
            cancelAnimationFrame(positionDragRafRef.current);
        positionDragRafRef.current = requestAnimationFrame(function () {
            positionDragRafRef.current = 0;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
                });
            });
        });
    };
    var onPositionDragEnd = function (e) {
        var _a;
        if (!positionDragRef.current)
            return;
        setIsDraggingSelf(false);
        if (!positionDragRef.current.hasMoved) {
            setSelectedTool(selectTool(e, id, selectedTool, false));
        }
        else if (dragStartPositionRef.current && currentPositionRef.current) {
            // Position changed, save state for undo/redo
            var oldPosition = dragStartPositionRef.current;
            // Get the new position from ref (updated during drag, avoids stale closure)
            var newPosition = currentPositionRef.current;
            if (oldPosition.x !== newPosition.x || oldPosition.y !== newPosition.y) {
                var entry = {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(oldPosition),
                    new: JSON.stringify(newPosition),
                };
                if (!registerMultiSelectSaveEntries(id, [entry])) {
                    saveState(entry);
                }
            }
        }
        if (positionDragRafRef.current) {
            cancelAnimationFrame(positionDragRafRef.current);
            positionDragRafRef.current = 0;
        }
        // Ensure final position is synced to context AND move clock to end of
        // manipulative array so it renders on top (matches Spinner/Dice/Timer).
        if (((_a = positionDragRef.current) === null || _a === void 0 ? void 0 : _a.hasMoved) && currentPositionRef.current) {
            var _b = currentPositionRef.current, finalX_1 = _b.x, finalY_1 = _b.y;
            setManipulative(function (prev) {
                var index = prev.findIndex(function (item) { return item.data.id === id; });
                if (index === -1) {
                    return prev.map(function (item) {
                        return item.data.id === id
                            ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: finalX_1, y: finalY_1 } }) }) : item;
                    });
                }
                var updated = __spreadArray([], prev, true);
                var movedItem = updated.splice(index, 1)[0];
                updated.push(__assign(__assign({}, movedItem), { data: __assign(__assign({}, movedItem.data), { center: { x: finalX_1, y: finalY_1 } }) }));
                return updated;
            });
        }
        positionDragRef.current.hasMoved = false;
        positionDragRef.current = null;
        dragStartPositionRef.current = null;
        currentPositionRef.current = null;
        window.removeEventListener('mousemove', onPositionDragMove);
        window.removeEventListener('touchmove', onPositionDragMove);
        window.removeEventListener('mouseup', onPositionDragEnd);
        window.removeEventListener('touchend', onPositionDragEnd);
        endMultiDrag();
    };
    // Special time calculation for digital display that floors minute instead of rounding
    // This prevents the digital display from showing the next minute before seconds reach 60
    var _digitalDisplayTime = useMemo(function () {
        var normalizedMinuteAngle = ((_minuteAngle % 360) + 360) % 360;
        var normalizedHourAngle = ((_hourAngle % 360) + 360) % 360;
        // Use Math.floor to always round down - minute only increments at 60 seconds
        var minutes = Math.floor(normalizedMinuteAngle / 6);
        minutes = minutes === 60 ? 0 : minutes;
        var hours_12 = Math.floor(normalizedHourAngle / 30);
        hours_12 = hours_12 === 0 ? 12 : hours_12;
        return {
            hour: hours_12,
            minute: minutes,
        };
    }, [_hourAngle, _minuteAngle]);
    var _arcColorGreen = '#006600';
    var _arcColorRed = '#ff3c38';
    var _arcBorder = '#707070';
    var _arcOpacity = elapsedFractionRef.current >= 0 ? 0.25 : 0.4;
    var _arcRadius = 120;
    var _arcCenter = { x: 130, y: 130 };
    //  const _arcRadius = clockDesign === 'numberWithMinutes' ? 241 : 213;
    //  const _arcCenter = clockDesign === 'numberWithMinutes' ? { x: 251, y: 251 } : { x: 223, y: 223 };
    var _arcPadding = 5;
    var _startAngle = elapsedFractionRef.current >= 0 ? (_l = elapsedReferenceMinuteAngleRef === null || elapsedReferenceMinuteAngleRef === void 0 ? void 0 : elapsedReferenceMinuteAngleRef.current) !== null && _l !== void 0 ? _l : 0 : _minuteAngle;
    var _endAngle = elapsedFractionRef.current >= 0 ? _minuteAngle : (_m = elapsedReferenceMinuteAngleRef === null || elapsedReferenceMinuteAngleRef === void 0 ? void 0 : elapsedReferenceMinuteAngleRef.current) !== null && _m !== void 0 ? _m : 0;
    return (_jsxs("div", { className: "analog_clockHolder flexItem ".concat(id), ref: wrapperRef, "data-manipulative-id": id, role: "presentation", onKeyDown: function (e) {
            var _a, _b, _c;
            if (!onWhiteBoard || panningEnable)
                return;
            // Context menu: Enter, Space, Shift+F10, ContextMenu key — only when dummy button is focused
            var isDummyBtn = (_a = e.target) === null || _a === void 0 ? void 0 : _a.closest('.dummy_btn');
            if (isDummyBtn &&
                (e.key === 'Enter' ||
                    e.key === ' ' ||
                    e.key === 'ContextMenu' ||
                    (e.shiftKey && e.key === 'F10'))) {
                e.preventDefault();
                e.stopPropagation();
                if (!selectedTool.includes(id)) {
                    setSelectedTool([id]);
                }
                var rect = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                if (rect) {
                    setContextMenu({ x: rect.right + 8, y: rect.top, manipulativeId: id });
                }
                return;
            }
            // Arrow keys: move clock ONLY when dummy button is focused (not hand sliders)
            if (isDummyBtn && !(lock === null || lock === void 0 ? void 0 : lock.positioning)) {
                var STEP = e.shiftKey ? 50 : 10;
                var delta = {
                    ArrowUp: { x: 0, y: -STEP, dir: t('text.up') },
                    ArrowDown: { x: 0, y: STEP, dir: t('text.down') },
                    ArrowLeft: { x: -STEP, y: 0, dir: t('text.left') },
                    ArrowRight: { x: STEP, y: 0, dir: t('text.right') },
                };
                if (delta[e.key]) {
                    e.preventDefault();
                    e.stopPropagation();
                    var d = delta[e.key];
                    var oldCenter = (_c = _clockData.center) !== null && _c !== void 0 ? _c : { x: 0, y: 0 };
                    var newCenter_1 = { x: oldCenter.x + d.x, y: oldCenter.y + d.y };
                    setManipulative(function (prev) {
                        return prev.map(function (m) {
                            return m.data.id === id ? __assign(__assign({}, m), { data: __assign(__assign({}, m.data), { center: newCenter_1 }) }) : m;
                        });
                    });
                    announce(t('text.manipulativeMoved', {
                        name: t('text.manipulativeClock'),
                        direction: d.dir,
                        x: Math.round(newCenter_1.x),
                        y: Math.round(newCenter_1.y),
                    }));
                }
            }
        }, style: {
            transform: onWhiteBoard ? "translate(-50%, -50%) scale(".concat(scaling, ")") : "scale(1)",
            height: "".concat((_o = _clockData.dimensions) === null || _o === void 0 ? void 0 : _o.height, "px"),
            width: "".concat((_p = _clockData.dimensions) === null || _p === void 0 ? void 0 : _p.width, "px"),
            left: onWhiteBoard ? "".concat(Math.round(_clockData.center.x), "px") : undefined,
            top: onWhiteBoard ? "".concat(Math.round(_clockData.center.y), "px") : undefined,
            // border: onWhiteBoard ? `2px solid ${isFocused ? 'black' : 'transparent'}` : undefined,
            borderRadius: "50%",
            boxShadow: onWhiteBoard ? "0 0 20px ".concat(isFocused ? '#5063AE' : 'transparent') : undefined,
            cursor: 'pointer',
            zIndex: isDraggingSelf ? 999 : undefined,
        }, children: [elapsedState && onWhiteBoard && (_jsx(ButtonComponent, { defaultCSS: "elapsedClickBtn", imgSrc: btnConfig.elapsedTimeTick.imgSrc, isActive: elapsedTime, ariaLabel: "text.elapsedTime", ariaPressed: elapsedTime, clickActive: true, controlActiveFromOutside: true, type: "imageType1", clickHandler: function () {
                    var _a;
                    var newElapsedTimeActive = !elapsedTime;
                    // Capture old state for undo (include time data for proper restoration)
                    var oldData = {
                        // Time data
                        hour: hour,
                        minute: minute,
                        period: period,
                        hourAngle: _hourAngle,
                        minuteAngle: _minuteAngle,
                        // Elapsed data
                        elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                            active: elapsedTime,
                            dragStarted: dragStartedState,
                            hoursCount: elapsedHours,
                            referenceAngle: elapsedReferenceMinuteAngleRef.current,
                            prevHourAngle: elapsedPrevHourAngleRef.current,
                            fraction: elapsedFractionRef.current,
                        }),
                    };
                    // Calculate new elapsed ref values if activating
                    var newElapsedReferenceAngle = newElapsedTimeActive ? _hourAngle * 12 : null;
                    var newElapsedPrevHourAngle = newElapsedTimeActive ? _hourAngle : null;
                    // New state (time stays the same, only elapsed state changes)
                    var newData = {
                        // Time data (unchanged)
                        hour: hour,
                        minute: minute,
                        period: period,
                        hourAngle: _hourAngle,
                        minuteAngle: _minuteAngle,
                        // Elapsed data
                        elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                            active: newElapsedTimeActive,
                            dragStarted: false,
                            hoursCount: 0,
                            referenceAngle: newElapsedReferenceAngle,
                            prevHourAngle: newElapsedPrevHourAngle,
                            fraction: 0,
                        }),
                    };
                    // Save undo/redo state
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(oldData),
                        new: JSON.stringify(newData),
                    });
                    // Update manipulative state (set flag to skip sync useEffects)
                    if (runJumpVisible) {
                        // Stop the RunJump timer when dragging starts
                        (_a = runJumpRef.current) === null || _a === void 0 ? void 0 : _a.handleRunStop();
                    }
                    isLocalElapsedUpdateRef.current = true;
                    setManipulative(function (prev) {
                        return prev.map(function (_man) {
                            if (_man.data.type === 'analog' && _man.data.id === id) {
                                return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), newData) });
                            }
                            return _man;
                        });
                    });
                    // Update local state
                    setElapsedHours(0);
                    elapsedHoursRef.current = 0;
                    elapsedFractionRef.current = 0;
                    elapsedReferenceMinuteAngleRef.current = null;
                    elapsedPrevMinuteAngleRef.current = null;
                    elapsedPrevHourAngleRef.current = null;
                    setRotationTooltip(function (prevTooltip) { return (__assign(__assign({}, prevTooltip), { visible: false, hours: 0 })); });
                    setDragStartedState(false);
                    isInitializingElapsedRef.current = false;
                    if (newElapsedTimeActive) {
                        // Use _hourAngle * 12 to match what drag system uses
                        var initialDragAngle = _hourAngle * 12;
                        elapsedReferenceMinuteAngleRef.current = initialDragAngle;
                        elapsedPrevMinuteAngleRef.current = initialDragAngle;
                        elapsedPrevHourAngleRef.current = _hourAngle;
                        // CRITICAL: Initialize totalMinuteAngleRef to match the reference
                        // This ensures timeUpdate calculations start from the correct position
                        totalMinuteAngleRef.current = initialDragAngle;
                        setTotalMinuteAngle(initialDragAngle);
                    }
                    setElapsedTime(newElapsedTimeActive);
                    // Focus minute hand (priority) or hour hand after activating elapsed tracking
                    if (newElapsedTimeActive) {
                        setTimeout(function () {
                            var _a;
                            var wrapper = document.querySelector("[data-manipulative-id=\"".concat(id, "\"]"));
                            var minuteHand = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.minute_hand');
                            var hourHand = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('.hour_hand');
                            (_a = (minuteHand !== null && minuteHand !== void 0 ? minuteHand : hourHand)) === null || _a === void 0 ? void 0 : _a.focus();
                        }, 150);
                    }
                } })), _jsxs("div", { style: {
                    height: "".concat((_q = _clockData.dimensions) === null || _q === void 0 ? void 0 : _q.height, "px"),
                    width: "".concat((_r = _clockData.dimensions) === null || _r === void 0 ? void 0 : _r.width, "px"),
                    position: 'relative',
                }, children: [_jsx("img", { src: getImageSrc(((_s = imageURL["".concat(analogType, "Clock")]) !== null && _s !== void 0 ? _s : Object.values(imageURL)[0]).imgSrc.normal), alt: "", "aria-hidden": true, ref: clockRef, style: { width: 'auto', height: '100%', userSelect: 'none' }, draggable: false }), movement === 'geared' && (_jsx("img", { className: "clockGear ".concat(onWhiteBoard ? 'whiteBoardGear' : ''), src: getImageSrc(btnConfig.clockGear.imgSrc.normal), "aria-hidden": true, draggable: false, alt: "" })), elapsedTime && movement === 'geared' && rotationTooltip.visible && onWhiteBoard && (_jsxs("div", { className: "handsHolder ".concat(clockDesign === 'numberWithMinutes' ? 'bigClock' : ''), "aria-hidden": true, children: [minutehandV && (_jsx("div", { className: "minute_hand flexItem ", style: { transform: "rotate(".concat(elapsedReferenceMinuteAngleRef.current, "deg)") }, children: _jsx("img", { style: { position: 'absolute' }, src: getImageSrc(btnConfig.staticMinuteHands.imgSrc.normal), draggable: false, alt: "" }) })), hourhandV && (_jsx("div", { className: "hour_hand flexItem ", style: { transform: "rotate(".concat(elapsedPrevHourAngleRef.current, "deg)") }, children: _jsx("img", { style: { position: 'absolute' }, src: (_t = getImageSrc(btnConfig.staticHourHands.imgSrc.normal)) !== null && _t !== void 0 ? _t : '', draggable: false, alt: "" }) }))] })), elapsedTime && movement === 'geared' && onWhiteBoard && (_jsxs("svg", { viewBox: "0 0 260 260", "aria-hidden": true, style: {
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                        }, children: [!dragStartedState && (_jsxs(_Fragment, { children: [_jsx(ArcPath, { startAngle: (_u = elapsedReferenceMinuteAngleRef === null || elapsedReferenceMinuteAngleRef === void 0 ? void 0 : elapsedReferenceMinuteAngleRef.current) !== null && _u !== void 0 ? _u : 0, endAngle: _minuteAngle + _arcPadding, radius: _arcRadius, center: _arcCenter, strokeWidth: 1, opacity: _arcOpacity, stroke: _arcBorder, fill: _arcColorGreen }), _jsx(ArcPath, { startAngle: _minuteAngle - _arcPadding, endAngle: (_v = elapsedReferenceMinuteAngleRef === null || elapsedReferenceMinuteAngleRef === void 0 ? void 0 : elapsedReferenceMinuteAngleRef.current) !== null && _v !== void 0 ? _v : 0, radius: _arcRadius, center: _arcCenter, strokeWidth: 1, opacity: _arcOpacity, stroke: _arcBorder, fill: _arcColorRed })] })), dragStartedState && (_jsxs(_Fragment, { children: [_jsx(ArcPath, { startAngle: _startAngle, endAngle: _endAngle, radius: _arcRadius, center: _arcCenter, strokeWidth: 1, opacity: _arcOpacity, stroke: _arcBorder, fullCircle: elapsedFractionRef.current >= 1 || elapsedFractionRef.current <= -1, fill: elapsedFractionRef.current >= 0 ? _arcColorGreen : _arcColorRed }), (elapsedFractionRef.current >= 1 || elapsedFractionRef.current <= -1) && (_jsx(ArcPath, { startAngle: 
                                        // rotationTooltip.hours > 0
                                        //   ?
                                        rotationTooltip.hours % 2 === 0 ? _endAngle : _startAngle, endAngle: 
                                        // rotationTooltip.hours > 0
                                        //   ?
                                        rotationTooltip.hours % 2 === 0 ? _startAngle : _endAngle, radius: _arcRadius, center: _arcCenter, strokeWidth: 1, opacity: rotationTooltip.hours >= 0 ? _arcOpacity : _arcOpacity, stroke: _arcBorder, fullCircle: Number.isInteger(elapsedFractionRef.current) &&
                                            elapsedFractionRef.current % 2 === 0, fill: rotationTooltip.hours >= 0 ? _arcColorGreen : _arcColorRed }))] }))] })), elapsedTime &&
                        movement === 'geared' &&
                        rotationTooltip.visible &&
                        rotationTooltip.hours !== 0 &&
                        onWhiteBoard && (_jsx("div", { className: "rotationTooltip ".concat(rotationTooltip.hours > 0 ? 'rotationTooltipPositive' : 'rotationTooltipNegative'), style: {
                            left: "".concat(rotationTooltip.x, "px"),
                            top: "".concat(rotationTooltip.y, "px"),
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            pointerEvents: 'none',
                        }, draggable: false, children: "".concat(rotationTooltip.hours > 0 ? '+' : '').concat(rotationTooltip.hours) })), _jsx("div", { className: "clockCenter", "aria-hidden": true }), _jsx(ButtonComponent, { defaultCSS: 'dummy_btn', normalCSS: "".concat(onWhiteBoard ? 'dummy_btnGrp' : 'disableGrp1'), text: "", role: onWhiteBoard && (hourhandV || minutehandV) ? 'group' : 'img', ariaLabel: (_w = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _w !== void 0 ? _w : clockAriaLabel, ariaRoledescription: onWhiteBoard ? t('text.manipulativeClock') : undefined, ariaHidden: !onWhiteBoard, clickHandler: function (e) {
                            var _a;
                            if (!onWhiteBoard)
                                return;
                            setSelectedTool(selectTool(e, id, selectedTool, false));
                            // VoiceOver synthetic clicks have detail === 0; real taps have detail === 1
                            var isVoiceOverClick = IS_TOUCH_DEVICE && e.detail === 0;
                            if (isVoiceOverClick) {
                                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect) {
                                    setContextMenu({ x: rect.right + 8, y: rect.top, manipulativeId: id });
                                }
                            }
                        }, isActive: false, onMouseDown: onPositionDragStart, onTouchStart: onPositionDragStart }), fractionOverlayParts && onWhiteBoard && (_jsxs("div", { ref: fractionOverlayRef, className: "fraction-overlay".concat(shadingEnabled ? ' shading-active' : ''), tabIndex: shadingEnabled ? 0 : -1, role: sectionMode ? 'group' : 'slider', "aria-label": sectionMode
                            ? t('text.fractionSectionNav', { total: fractionOverlayParts })
                            : t('text.fractionOverlayRotation'), "aria-valuenow": sectionMode ? undefined : Math.round(fractionOverlayRotation), "aria-valuemin": sectionMode ? undefined : 0, "aria-valuemax": sectionMode ? undefined : 359, onKeyDown: function (e) {
                            var _a, _b;
                            var isArrow = e.key === 'ArrowLeft' ||
                                e.key === 'ArrowRight' ||
                                e.key === 'ArrowUp' ||
                                e.key === 'ArrowDown';
                            if (sectionMode) {
                                // Section navigation mode
                                if (isArrow) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    var total_1 = fractionOverlayParts !== null && fractionOverlayParts !== void 0 ? fractionOverlayParts : 1;
                                    var dir_1 = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
                                    setFocusedSectionIndex(function (prev) {
                                        var next = (prev + dir_1 + total_1) % total_1;
                                        var isShaded = fractionOverlayColors[next] && fractionOverlayColors[next] !== 'transparent';
                                        announce(t('text.fractionSectionInfo', {
                                            index: next + 1,
                                            total: total_1,
                                            state: isShaded ? t('text.shaded') : t('text.unshaded'),
                                        }));
                                        return next;
                                    });
                                }
                                else if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (shadingEnabled) {
                                        // Toggle shade — reuse existing click logic via colors array
                                        var newColors = __spreadArray([], (fractionOverlayColors.length > 0
                                            ? fractionOverlayColors
                                            : Array(fractionOverlayParts).fill('transparent')), true);
                                        var current = newColors[focusedSectionIndex];
                                        newColors[focusedSectionIndex] =
                                            current === ColorEnum[color] ? 'transparent' : ColorEnum[color];
                                        updateFractionOverlayColors(newColors);
                                        var isNowShaded = newColors[focusedSectionIndex] !== 'transparent';
                                        announce(t('text.fractionSectionToggled', {
                                            index: focusedSectionIndex + 1,
                                            state: isNowShaded ? t('text.shaded') : t('text.unshaded'),
                                        }));
                                    }
                                }
                                else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSectionMode(false);
                                    setFocusedSectionIndex(0);
                                    (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.dummy_btn')) === null || _b === void 0 ? void 0 : _b.focus();
                                }
                            }
                            else {
                                // Rotation mode
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSectionMode(true);
                                    setFocusedSectionIndex(0);
                                    var isShaded = fractionOverlayColors[0] && fractionOverlayColors[0] !== 'transparent';
                                    announce(t('text.fractionSectionEntered', {
                                        total: fractionOverlayParts,
                                        state: isShaded ? t('text.shaded') : t('text.unshaded'),
                                    }));
                                }
                                else if (isArrow) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    var step = e.shiftKey ? 10 : 1;
                                    var delta = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? step : -step;
                                    var newRotation = (((fractionOverlayRotation + delta) % 360) + 360) % 360;
                                    saveState({
                                        type: id,
                                        id: id.split('_')[1],
                                        propertyName: 'fractionOverlayRotation',
                                        old: JSON.stringify(fractionOverlayRotation),
                                        new: JSON.stringify(newRotation),
                                    });
                                    updateFractionOverlayRotation(newRotation);
                                    announce(t('text.fractionOverlayRotatedTo', { degrees: Math.round(newRotation) }));
                                }
                            }
                        }, onBlur: function () {
                            if (sectionMode) {
                                setSectionMode(false);
                                setFocusedSectionIndex(0);
                            }
                        }, style: {
                            transform: "rotate(".concat(fractionOverlayRotation, "deg)"),
                            cursor: onWhiteBoard
                                ? (lock === null || lock === void 0 ? void 0 : lock.positioning) || panningEnable
                                    ? 'inherit'
                                    : 'pointer'
                                : 'inherit',
                        }, children: [_jsx(CirclePartitionSVG, { initialColor: "transparent", selectedColor: ColorEnum[color], numberofpartition: fractionOverlayParts, disabled: !shadingEnabled, storedColors: fractionOverlayColors.length > 0 ? fractionOverlayColors : undefined, onColorsChange: updateFractionOverlayColors, focusedIndex: sectionMode ? focusedSectionIndex : null }), _jsxs("div", { className: "fractionOverlay-rotateHandle", children: [_jsx("div", { className: "fractionOverlay-rotateLine" }), _jsx(ButtonComponent, { imgSrc: {
                                            normal: './assets/images/manipulative/patternShape/Rotate_N.svg',
                                            hover: './assets/images/manipulative/patternShape/Rotate_H.svg',
                                        }, defaultCSS: "fractionOverlay-rotateBtn", ariaLabel: t('text.rotateFractionOverlay'), onMouseDown: onRotateHandleDown, onTouchStart: onRotateHandleDown, type: 'imageType1' })] })] })), _jsxs("div", { className: "handsHolder ".concat(clockDesign === 'numberWithMinutes' ? 'bigClock' : ''), children: [minutehandV && (_jsx(_Fragment, { children: _jsxs("div", { className: "minute_hand flexItem ", style: {
                                        transform: "rotate(".concat(_minuteAngle, "deg)"),
                                        cursor: onWhiteBoard ? (panningEnable ? 'inherit' : 'pointer') : 'inherit',
                                    }, "aria-hidden": !onWhiteBoard || undefined, tabIndex: onWhiteBoard && !panningEnable ? 0 : -1, role: "slider", "aria-label": t('text.minuteHand'), "aria-describedby": "clock-minute-hand-instructions", "aria-valuenow": Math.round((((_minuteAngle % 360) + 360) % 360) / 6) % 60, "aria-valuemin": 0, "aria-valuemax": 59, onMouseDown: function (e) { return startDrag('minute', e); }, onTouchStart: function (e) { return startDrag('minute', e); }, onKeyDown: handleMinuteHandKeyDown, onKeyUp: handleHandKeyUp, children: [_jsx("img", { style: { position: 'absolute' }, src: btnConfig.minuteHands.imgSrc.normal, "aria-hidden": true, draggable: false, alt: "" }), handLabelV && (_jsx("div", { className: "handLabel ".concat(onWhiteBoard ? 'handLabelMinuteOnWhiteBoard' : ''), children: t('text.minute') }))] }) })), hourhandV && (_jsx(_Fragment, { children: _jsxs("div", { className: "hour_hand flexItem ", style: {
                                        transform: "rotate(".concat(_hourAngle, "deg)"),
                                        cursor: onWhiteBoard ? (panningEnable ? 'inherit' : 'pointer') : 'inherit',
                                    }, "aria-hidden": !onWhiteBoard || undefined, tabIndex: onWhiteBoard && !panningEnable ? 0 : -1, role: "slider", "aria-label": t('text.hourHand'), "aria-describedby": "clock-hour-hand-instructions", "aria-valuenow": Math.round((((_hourAngle % 360) + 360) % 360) / 30) % 12 || 12, "aria-valuemin": 1, "aria-valuemax": 12, onMouseDown: function (e) { return startDrag('hour', e); }, onTouchStart: function (e) { return startDrag('hour', e); }, onKeyDown: handleHourHandKeyDown, onKeyUp: handleHandKeyUp, children: [_jsx("img", { style: { position: 'absolute' }, src: btnConfig.hourHands.imgSrc.normal, "aria-hidden": true, draggable: false, alt: "" }), handLabelV && (_jsx("div", { className: "handLabel ".concat(onWhiteBoard ? 'handLabelHourOnWhiteBoard' : ''), children: t('text.hour') }))] }) })), secondsVisible && runJumpVisible && (_jsx(_Fragment, { children: _jsx("div", { className: "second_hand flexItem ", "aria-hidden": true, style: {
                                        transform: "rotate(".concat(_secondAngle, "deg)"),
                                    }, children: _jsx("img", { style: { position: 'absolute', bottom: '-17px' }, src: btnConfig.secondsHand.imgSrc.normal, draggable: false, alt: "" }) }) }))] })] }), onWhiteBoard && (_jsxs("div", { style: {}, className: "clockFunctionality", children: [digitalClockVisible && (_jsx(DigitalClock, { _clockData: {
                            hour: _digitalDisplayTime.hour,
                            minute: _digitalDisplayTime.minute,
                            period: currentPeriod,
                            timeFormat: _clockData.timeFormat,
                        }, movement: movement, onClockDataChange: function (data) {
                            var _a, _b, _c, _d;
                            if (runJumpVisible && runJumpRef.current) {
                                runJumpRef.current.handleRunStop();
                            }
                            var clonedData = JSON.parse(JSON.stringify(data));
                            // Save state for undo/redo
                            var oldTimeData = {
                                hour: hour,
                                minute: minute,
                                period: period,
                                hourAngle: notGearedHourAngleRef.current,
                                minuteAngle: notGearedMinuteAngleRef.current,
                                timeFormat: (_a = _clockData.timeFormat) !== null && _a !== void 0 ? _a : '12h',
                            };
                            var newTimeData = {
                                hour: data.hour,
                                minute: data.minute,
                                period: data.period,
                                hourAngle: data.hourAngle,
                                minuteAngle: data.minuteAngle,
                                timeFormat: data.timeFormat,
                            };
                            // Handle elapsed time state if active
                            var isElapsedActive = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                            var newElapsedHours = elapsedHoursRef.current;
                            var newTotalMinuteAngle = totalMinuteAngleRef.current;
                            if (isElapsedActive) {
                                // Capture old elapsed state for undo/redo
                                oldTimeData.elapsedTimeDisplay = mergeElapsed(clockElapsedTimeDisplay, {
                                    dragStarted: dragStartedState,
                                    hoursCount: elapsedHoursRef.current,
                                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                    prevHourAngle: elapsedPrevHourAngleRef.current,
                                    totalMinuteAngle: totalMinuteAngleRef.current,
                                    fraction: elapsedFractionRef.current,
                                });
                                // Convert old time (props) to 24h total minutes
                                var oldHour24 = period === 'AM' ? (hour === 12 ? 0 : hour) : hour === 12 ? 12 : hour + 12;
                                var oldTotalMinutes = oldHour24 * 60 + minute;
                                // Convert new time (digital clock data) to 24h total minutes
                                var newHour24 = data.period === 'AM'
                                    ? data.hour === 12
                                        ? 0
                                        : data.hour
                                    : data.hour === 12
                                        ? 12
                                        : data.hour + 12;
                                var newTotalMinutes = newHour24 * 60 + data.minute;
                                // Calculate minute difference with day wrap-around handling
                                var minuteDiff = newTotalMinutes - oldTotalMinutes;
                                if (minuteDiff > 720)
                                    minuteDiff -= 1440; // More than 12h forward → go backward
                                if (minuteDiff < -720)
                                    minuteDiff += 1440; // More than 12h backward → go forward
                                // Convert to angle change and add to current totalMinuteAngle
                                var angleDiff = minuteDiff * 6;
                                newTotalMinuteAngle = totalMinuteAngleRef.current + angleDiff;
                                // Calculate new elapsed hours based on crossing detection
                                var referenceAngle = (_b = elapsedReferenceMinuteAngleRef.current) !== null && _b !== void 0 ? _b : 0;
                                var oldAngleFromRef = totalMinuteAngleRef.current - referenceAngle;
                                var newAngleFromRef = newTotalMinuteAngle - referenceAngle;
                                var oldHours = Math.trunc(oldAngleFromRef / 360);
                                var newHours = Math.trunc(newAngleFromRef / 360);
                                if (newHours !== oldHours) {
                                    newElapsedHours = newHours;
                                }
                                elapsedFractionRef.current = computeElapsedFraction(newTotalMinuteAngle, referenceAngle, newElapsedHours, elapsedFractionRef.current, angleDiff);
                                // Capture new elapsed state for undo/redo
                                newTimeData.elapsedTimeDisplay = mergeElapsed(oldTimeData.elapsedTimeDisplay, {
                                    dragStarted: true,
                                    hoursCount: newElapsedHours,
                                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                    prevHourAngle: elapsedPrevHourAngleRef.current,
                                    totalMinuteAngle: newTotalMinuteAngle,
                                    fraction: elapsedFractionRef.current,
                                });
                            }
                            // Only save if time or timeFormat actually changed
                            if (oldTimeData.hour !== newTimeData.hour ||
                                oldTimeData.minute !== newTimeData.minute ||
                                oldTimeData.period !== newTimeData.period ||
                                oldTimeData.hourAngle !== newTimeData.hourAngle ||
                                oldTimeData.minuteAngle !== newTimeData.minuteAngle ||
                                oldTimeData.timeFormat !== newTimeData.timeFormat ||
                                (isElapsedActive &&
                                    ((_c = oldTimeData.elapsedTimeDisplay) === null || _c === void 0 ? void 0 : _c.totalMinuteAngle) !== ((_d = newTimeData.elapsedTimeDisplay) === null || _d === void 0 ? void 0 : _d.totalMinuteAngle))) {
                                saveState({
                                    type: id,
                                    id: id.split('_')[1],
                                    propertyName: 'completeData',
                                    old: JSON.stringify(oldTimeData),
                                    new: JSON.stringify(newTimeData),
                                });
                            }
                            // When elapsed time is active, update the internal angle states
                            // because the sync useEffect is skipped when elapsedTime && dragStartedState
                            if (isElapsedActive) {
                                isLocalElapsedUpdateRef.current = true;
                                // Update all the refs and state
                                totalMinuteAngleRef.current = newTotalMinuteAngle;
                                setTotalMinuteAngle(newTotalMinuteAngle);
                                setHourAngle(data.hourAngle);
                                setMinuteAngle(data.minuteAngle);
                                elapsedPrevMinuteAngleRef.current = newTotalMinuteAngle;
                                elapsedHoursRef.current = newElapsedHours;
                                setElapsedHours(newElapsedHours);
                                if (!dragStartedState) {
                                    setDragStartedState(true);
                                }
                                // Update tooltip position
                                updateRotationTooltipPositionFromAngle(newTotalMinuteAngle, newElapsedHours, true);
                                // Update period
                                periodRef.current = data.period;
                                setCurrentPeriod(data.period);
                                prevHourForPeriodRef.current = data.hour;
                            }
                            setManipulative(function (prev) {
                                return prev.map(function (_man) {
                                    return _man.data.type === 'analog' && _man.data.id === id
                                        ? __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), clonedData), (isElapsedActive
                                                ? {
                                                    elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                                        dragStarted: true,
                                                        hoursCount: newElapsedHours,
                                                        totalMinuteAngle: newTotalMinuteAngle,
                                                        fraction: elapsedFractionRef.current,
                                                    }),
                                                }
                                                : {})) }) : _man;
                                });
                            });
                        } })), runJumpVisible && (_jsx(RunJump, { ref: runJumpRef, currentHour: hour, currentMinute: minute, currentPeriod: period, initialTab: clockRunJumpTab, initialJumpValue: clockRunJumpValue, onChange: function (event, data) {
                            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                            if (event === 'jump' && data.id === 'jump') {
                                // Calculate old and new time for undo/redo
                                var oldTimeData = {
                                    hour: hour,
                                    minute: minute,
                                    period: period,
                                    hourAngle: notGearedHourAngleRef.current,
                                    minuteAngle: notGearedMinuteAngleRef.current,
                                };
                                var result_1 = data.addBy
                                    ? addTime({ hour: hour, minute: minute, period: period }, { hours: 0, minutes: +data.jumpBy })
                                    : addTime({ hour: hour, minute: minute, period: period }, { hours: 0, minutes: -data.jumpBy });
                                // Calculate the change in minutes and degrees
                                var jumpMinutes = data.addBy ? +data.jumpBy : -data.jumpBy;
                                var jumpDegrees = jumpMinutes * 6;
                                var newTimeData = {
                                    hour: movement === 'geared' ? result_1.hour : hour,
                                    minute: result_1.minute,
                                    period: result_1.period,
                                    hourAngle: notGearedHourAngleRef.current,
                                    minuteAngle: result_1.minute * 6,
                                };
                                // Handle elapsed time state if active
                                // Use same condition as timeUpdate: check if reference is set, not dragStartedState
                                var newElapsedHours_1 = elapsedHoursRef.current;
                                var newTotalMinuteAngle_1 = totalMinuteAngleRef.current;
                                var isElapsedActive_1 = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                                if (isElapsedActive_1) {
                                    // Capture old elapsed state
                                    oldTimeData.elapsedTimeDisplay = mergeElapsed(clockElapsedTimeDisplay, {
                                        dragStarted: dragStartedState,
                                        hoursCount: elapsedHoursRef.current,
                                        referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                        prevHourAngle: elapsedPrevHourAngleRef.current,
                                        totalMinuteAngle: totalMinuteAngleRef.current,
                                        fraction: elapsedFractionRef.current,
                                    });
                                    // Calculate new total angle after jump
                                    newTotalMinuteAngle_1 = totalMinuteAngleRef.current + jumpDegrees;
                                    // Calculate new elapsed hours based on crossing detection
                                    var referenceAngle = (_a = elapsedReferenceMinuteAngleRef.current) !== null && _a !== void 0 ? _a : 0;
                                    var oldAngleFromRef = totalMinuteAngleRef.current - referenceAngle;
                                    var newAngleFromRef = newTotalMinuteAngle_1 - referenceAngle;
                                    // Calculate hours based on full rotations from reference
                                    var oldHours = Math.trunc(oldAngleFromRef / 360);
                                    var newHours = Math.trunc(newAngleFromRef / 360);
                                    // Update elapsed hours if we crossed hour boundaries
                                    if (newHours !== oldHours) {
                                        newElapsedHours_1 = newHours;
                                    }
                                    elapsedFractionRef.current = computeElapsedFraction(newTotalMinuteAngle_1, referenceAngle, newElapsedHours_1, elapsedFractionRef.current, jumpDegrees);
                                    // Capture new elapsed state
                                    newTimeData.elapsedTimeDisplay = mergeElapsed(oldTimeData.elapsedTimeDisplay, {
                                        dragStarted: true,
                                        hoursCount: newElapsedHours_1,
                                        referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                        prevHourAngle: elapsedPrevHourAngleRef.current,
                                        totalMinuteAngle: newTotalMinuteAngle_1,
                                        fraction: elapsedFractionRef.current,
                                    });
                                }
                                // Save state for undo/redo
                                if (oldTimeData.hour !== newTimeData.hour ||
                                    oldTimeData.minute !== newTimeData.minute ||
                                    oldTimeData.period !== newTimeData.period ||
                                    (isElapsedActive_1 &&
                                        ((_b = oldTimeData.elapsedTimeDisplay) === null || _b === void 0 ? void 0 : _b.totalMinuteAngle) !== ((_c = newTimeData.elapsedTimeDisplay) === null || _c === void 0 ? void 0 : _c.totalMinuteAngle))) {
                                    saveState({
                                        type: id,
                                        id: id.split('_')[1],
                                        propertyName: 'completeData',
                                        old: JSON.stringify(oldTimeData),
                                        new: JSON.stringify(newTimeData),
                                    });
                                }
                                // Update elapsed time refs if active (use same condition as above)
                                if (isElapsedActive_1) {
                                    // Mark drag as started if not already (like timeUpdate does)
                                    if (!dragStartedState) {
                                        setDragStartedState(true);
                                    }
                                    isLocalElapsedUpdateRef.current = true;
                                    totalMinuteAngleRef.current = newTotalMinuteAngle_1;
                                    setTotalMinuteAngle(newTotalMinuteAngle_1);
                                    elapsedHoursRef.current = newElapsedHours_1;
                                    setElapsedHours(newElapsedHours_1);
                                    elapsedPrevMinuteAngleRef.current = newTotalMinuteAngle_1;
                                    // Update visual angles (handle negative angles properly)
                                    var normalizedMinuteAngle = ((newTotalMinuteAngle_1 % 360) + 360) % 360;
                                    var hourAngleFromTotal = newTotalMinuteAngle_1 / 12;
                                    setMinuteAngle(normalizedMinuteAngle);
                                    setHourAngle(hourAngleFromTotal);
                                    // Update tooltip
                                    updateRotationTooltipPositionFromAngle(newTotalMinuteAngle_1, newElapsedHours_1, true);
                                }
                                setManipulative(function (prev) {
                                    return prev.map(function (_man) {
                                        if (_man.data.type === 'analog' && _man.data.id === id) {
                                            return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), { hour: movement === 'geared' ? result_1.hour : _man.data.hour, minute: result_1.minute, period: result_1.period }), (isElapsedActive_1
                                                    ? {
                                                        elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                                            dragStarted: true,
                                                            hoursCount: newElapsedHours_1,
                                                            totalMinuteAngle: newTotalMinuteAngle_1,
                                                            fraction: elapsedFractionRef.current,
                                                        }),
                                                    }
                                                    : {})) });
                                        }
                                        return _man;
                                    });
                                });
                                // Announce new time after jump
                                var jumpTimeStr = "".concat(movement === 'geared' ? result_1.hour : hour, ":").concat(String(Math.round(result_1.minute)).padStart(2, '0'), " ").concat(result_1.period);
                                announce(t('text.timeSetTo', { time: jumpTimeStr }));
                            }
                            else if (event === 'toggleRun' && data.id === 'toggleRun') {
                                setSecondsVisible(data.runActive);
                                // Reset seconds to 0 when timer stops
                                if (!data.runActive) {
                                    setSecondAngle(0);
                                }
                                else if (data.runActive && clockRuntimeSeconds && clockRuntimeSeconds > 0) {
                                    // When run tab becomes active (e.g., from undo restoring tab),
                                    // restore seconds display from manipulative data since the
                                    // clockRuntimeSeconds sync useEffect won't fire if the value didn't change
                                    setSecondAngle((clockRuntimeSeconds % 60) * 6);
                                    if (runJumpRef.current) {
                                        runJumpRef.current.setRuntimeSeconds(clockRuntimeSeconds);
                                    }
                                }
                                // Initialize totalMinuteAngleRef ONLY on first start, not on resume
                                if (elapsedTime) {
                                    if (data.runActive) {
                                        // Only initialize if this is the first start (dragStartedState is false)
                                        if (!dragStartedState) {
                                            // CRITICAL: Use the SAME reference angle that was stored when elapsed time was activated
                                            // to avoid mismatches in crossing calculations
                                            var initialTotalMinuteAngle = (_d = elapsedReferenceMinuteAngleRef.current) !== null && _d !== void 0 ? _d : _hourAngle * 12;
                                            // Set flag to prevent useEffect from resetting angles during initialization
                                            isInitializingElapsedRef.current = true;
                                            // Initialize totalMinuteAngleRef to match the reference coordinate system
                                            totalMinuteAngleRef.current = initialTotalMinuteAngle;
                                            setTotalMinuteAngle(initialTotalMinuteAngle);
                                            // CRITICAL: Sync elapsedPrevMinuteAngleRef to prevent false crossings
                                            elapsedPrevMinuteAngleRef.current = initialTotalMinuteAngle;
                                            setDragStartedState(true);
                                            // Show tooltip at starting position (should be 0 hours initially)
                                            updateRotationTooltipPositionFromAngle(initialTotalMinuteAngle, 0, // Start with 0 hours
                                            true);
                                            // Clear the initialization flag after state updates complete
                                            setTimeout(function () {
                                                isInitializingElapsedRef.current = false;
                                            }, 0);
                                        }
                                        else {
                                            // Sync elapsedPrevMinuteAngleRef on resume to ensure consistency
                                            elapsedPrevMinuteAngleRef.current = totalMinuteAngleRef.current;
                                        }
                                        // If resuming (dragStartedState is true), keep existing totalMinuteAngleRef
                                    }
                                    else {
                                        // Pausing - keep tooltip visible to show elapsed time
                                    }
                                }
                            }
                            else if (event === 'timeUpdate' && data.id === 'timeUpdate') {
                                // Handle elapsed time tracking if active (similar to jump handler)
                                if (elapsedTime && elapsedReferenceMinuteAngleRef.current !== null) {
                                    // Mark drag as started if not already
                                    if (!dragStartedState) {
                                        setDragStartedState(true);
                                    }
                                    // Calculate the new minute angle from the updated time
                                    var newAngles = calculateAnglesFromTime(data.hour, data.minute);
                                    var newNormalizedAngle = newAngles.minuteAngle; // 0-360°
                                    // Calculate delta from current position (handle 360° wrap)
                                    var currentTotalAngle = totalMinuteAngleRef.current;
                                    var prevNormalizedAngle = ((currentTotalAngle % 360) + 360) % 360;
                                    var delta = newNormalizedAngle - prevNormalizedAngle;
                                    // Handle crossing 0/360 boundary
                                    if (delta > 180)
                                        delta -= 360; // Crossed backward
                                    if (delta < -180)
                                        delta += 360; // Crossed forward
                                    // Calculate new total angle
                                    var newTotalMinuteAngle = currentTotalAngle + delta;
                                    // Calculate elapsed hours based on full rotations from reference (like jump handler)
                                    var referenceAngle = elapsedReferenceMinuteAngleRef.current;
                                    var oldAngleFromRef = currentTotalAngle - referenceAngle;
                                    var newAngleFromRef = newTotalMinuteAngle - referenceAngle;
                                    var oldHours = Math.trunc(oldAngleFromRef / 360);
                                    var newHours = Math.trunc(newAngleFromRef / 360);
                                    // Update elapsed hours if we crossed hour boundaries
                                    if (newHours !== oldHours) {
                                        elapsedHoursRef.current = newHours;
                                        setElapsedHours(newHours);
                                    }
                                    elapsedFractionRef.current = computeElapsedFraction(newTotalMinuteAngle, referenceAngle, elapsedHoursRef.current, elapsedFractionRef.current, delta);
                                    // Update refs
                                    totalMinuteAngleRef.current = newTotalMinuteAngle;
                                    setTotalMinuteAngle(newTotalMinuteAngle);
                                    elapsedPrevMinuteAngleRef.current = newTotalMinuteAngle;
                                    // Update visual angles
                                    var normalizedMinuteAngle = ((newTotalMinuteAngle % 360) + 360) % 360;
                                    var hourAngleFromTotal = newTotalMinuteAngle / 12;
                                    setMinuteAngle(normalizedMinuteAngle);
                                    setHourAngle(hourAngleFromTotal);
                                    // Update tooltip
                                    updateRotationTooltipPositionFromAngle(newTotalMinuteAngle, elapsedHoursRef.current, true);
                                }
                                if (movement === 'geared') {
                                    // Update refs to track current time (avoids stale closure in pause/play handlers)
                                    currentHourRef.current = data.hour;
                                    currentMinuteRef.current = data.minute;
                                    periodRef.current = data.period;
                                    // Update local period state for real-time DigitalClock display
                                    setCurrentPeriod(data.period);
                                    // Check if elapsed time tracking is active using REF (not state) to avoid stale closure
                                    var isElapsedTrackingActive_1 = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                                    // Set flag to prevent sync useEffect from resetting refs during continuous updates
                                    if (isElapsedTrackingActive_1) {
                                        isLocalElapsedUpdateRef.current = true;
                                    }
                                    setManipulative(function (prev) {
                                        return prev.map(function (_man) {
                                            if (_man.data.type === 'analog' && _man.data.id === id) {
                                                return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), { hour: data.hour, minute: data.minute, period: data.period }), (isElapsedTrackingActive_1
                                                        ? {
                                                            elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                                                dragStarted: true,
                                                                hoursCount: elapsedHoursRef.current,
                                                                totalMinuteAngle: totalMinuteAngleRef.current,
                                                                fraction: elapsedFractionRef.current,
                                                            }),
                                                        }
                                                        : {})) });
                                            }
                                            return _man;
                                        });
                                    });
                                }
                            }
                            else if (event === 'secondsUpdate' && data.id === 'secondsUpdate') {
                                setSecondAngle(data.seconds * 6);
                            }
                            else if (event === 'runAction' && data.id === 'runAction') {
                                var actionData_1 = data;
                                if (actionData_1.action === 'play') {
                                    // Capture start time when run starts (only if not already running/paused)
                                    // Use refs instead of props to get actual current time (avoids stale closure)
                                    if (!runStartTimeRef.current) {
                                        // Check if elapsed time tracking is active using REF
                                        var isElapsedTrackingActive = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                                        runStartTimeRef.current = __assign({ hour: currentHourRef.current, minute: currentMinuteRef.current, period: periodRef.current, hourAngle: notGearedHourAngleRef.current, minuteAngle: notGearedMinuteAngleRef.current, runtimeSeconds: (_f = (_e = runJumpRef.current) === null || _e === void 0 ? void 0 : _e.getRuntimeSeconds()) !== null && _f !== void 0 ? _f : 0 }, (isElapsedTrackingActive
                                            ? {
                                                elapsedTimeDisplay: mergeElapsed(clockElapsedTimeDisplay, {
                                                    dragStarted: true,
                                                    hoursCount: elapsedHoursRef.current,
                                                    referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                                    prevHourAngle: elapsedPrevHourAngleRef.current,
                                                    totalMinuteAngle: totalMinuteAngleRef.current,
                                                    fraction: elapsedFractionRef.current,
                                                }),
                                            }
                                            : {}));
                                    }
                                }
                                else if (actionData_1.action === 'stop' || actionData_1.action === 'pause') {
                                    // Save undo/redo state when timer stops or pauses
                                    if (runStartTimeRef.current) {
                                        var oldTimeData = runStartTimeRef.current;
                                        // Use refs instead of props to get the actual current time (avoids stale closure)
                                        var newTimeData_2 = {
                                            hour: currentHourRef.current,
                                            minute: currentMinuteRef.current,
                                            period: periodRef.current,
                                            hourAngle: notGearedHourAngleRef.current,
                                            minuteAngle: notGearedMinuteAngleRef.current,
                                            runtimeSeconds: (_h = (_g = runJumpRef.current) === null || _g === void 0 ? void 0 : _g.getRuntimeSeconds()) !== null && _h !== void 0 ? _h : 0,
                                        };
                                        // Check if elapsed time tracking is active using REF to avoid stale closure
                                        var isElapsedTrackingActive_2 = elapsedTime && elapsedReferenceMinuteAngleRef.current !== null;
                                        // Include elapsed time state if active
                                        if (isElapsedTrackingActive_2) {
                                            newTimeData_2.elapsedTimeDisplay = mergeElapsed(clockElapsedTimeDisplay, {
                                                dragStarted: true,
                                                hoursCount: elapsedHoursRef.current,
                                                referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                                prevHourAngle: elapsedPrevHourAngleRef.current,
                                                totalMinuteAngle: totalMinuteAngleRef.current,
                                                fraction: elapsedFractionRef.current,
                                            });
                                        }
                                        // Check if time or elapsed time changed
                                        var timeChanged = oldTimeData.hour !== newTimeData_2.hour ||
                                            oldTimeData.minute !== newTimeData_2.minute ||
                                            oldTimeData.period !== newTimeData_2.period;
                                        var elapsedChanged = isElapsedTrackingActive_2 &&
                                            ((_j = oldTimeData.elapsedTimeDisplay) === null || _j === void 0 ? void 0 : _j.totalMinuteAngle) !== ((_k = newTimeData_2.elapsedTimeDisplay) === null || _k === void 0 ? void 0 : _k.totalMinuteAngle);
                                        // Only save if something actually changed
                                        if (timeChanged || elapsedChanged) {
                                            saveState({
                                                type: id,
                                                id: id.split('_')[1],
                                                propertyName: 'completeData',
                                                old: JSON.stringify(oldTimeData),
                                                new: JSON.stringify(newTimeData_2),
                                            });
                                            // Update manipulative with runtimeSeconds and elapsed time state
                                            // NOTE: Do NOT set isLocalElapsedUpdateRef here. The elapsed data
                                            // written here is the same as what the last timeUpdate tick already set,
                                            // so the elapsed sync useEffect won't fire (deps unchanged). Setting the
                                            // flag without the useEffect firing to reset it would leave it stuck as
                                            // true, causing the next undo's sync useEffect to be incorrectly skipped.
                                            setManipulative(function (prev) {
                                                return prev.map(function (_man) {
                                                    if (_man.data.type === 'analog' && _man.data.id === id) {
                                                        return __assign(__assign({}, _man), { data: __assign(__assign(__assign({}, _man.data), { runtimeSeconds: actionData_1.action === 'stop' ? 0 : newTimeData_2.runtimeSeconds }), (isElapsedTrackingActive_2
                                                                ? {
                                                                    elapsedTimeDisplay: mergeElapsed(_man.data.elapsedTimeDisplay, {
                                                                        dragStarted: true,
                                                                        hoursCount: elapsedHoursRef.current,
                                                                        referenceAngle: elapsedReferenceMinuteAngleRef.current,
                                                                        prevHourAngle: elapsedPrevHourAngleRef.current,
                                                                        totalMinuteAngle: totalMinuteAngleRef.current,
                                                                        fraction: elapsedFractionRef.current,
                                                                    }),
                                                                }
                                                                : {})) });
                                                    }
                                                    return _man;
                                                });
                                            });
                                        }
                                        // Clear the ref after saving (for both stop and pause)
                                        // This ensures resume will capture a new start time
                                        runStartTimeRef.current = null;
                                    }
                                    // Announce current time on pause/stop
                                    var currentTimeStr = "".concat(currentHourRef.current, ":").concat(String(Math.round(currentMinuteRef.current)).padStart(2, '0'), " ").concat(periodRef.current);
                                    announce(t('text.timeSetTo', { time: currentTimeStr }));
                                }
                            }
                            else if (event === 'tabChange' && data.id === 'tabChange') {
                                var tabData_1 = data;
                                // Save undo/redo state for tab change
                                // Note: Values must be JSON.stringify'd because stateManagerHelper uses JSON.parse
                                saveState({
                                    type: id,
                                    id: id.split('_')[1],
                                    propertyName: 'runJumpTab',
                                    old: JSON.stringify(tabData_1.previousTab),
                                    new: JSON.stringify(tabData_1.tab),
                                });
                                // Update manipulative state
                                setManipulative(function (prev) {
                                    return prev.map(function (_man) {
                                        if (_man.data.type === 'analog' && _man.data.id === id) {
                                            return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { runJumpTab: tabData_1.tab }) });
                                        }
                                        return _man;
                                    });
                                });
                            }
                            else if (event === 'jumpValueChange' && data.id === 'jumpValueChange') {
                                var jumpValueData_1 = data;
                                saveState({
                                    type: id,
                                    id: id.split('_')[1],
                                    propertyName: 'runJumpValue',
                                    old: JSON.stringify(jumpValueData_1.previousJumpBy),
                                    new: JSON.stringify(jumpValueData_1.jumpBy),
                                });
                                setManipulative(function (prev) {
                                    return prev.map(function (_man) {
                                        if (_man.data.type === 'analog' && _man.data.id === id) {
                                            return __assign(__assign({}, _man), { data: __assign(__assign({}, _man.data), { runJumpValue: jumpValueData_1.jumpBy }) });
                                        }
                                        return _man;
                                    });
                                });
                            }
                        } }))] })), _jsx("div", { id: "clock-minute-hand-instructions", className: "srOnly", "aria-hidden": !onWhiteBoard || undefined, children: t('text.minuteHandInstructions') }), _jsx("div", { id: "clock-hour-hand-instructions", className: "srOnly", "aria-hidden": !onWhiteBoard || undefined, children: t('text.hourHandInstructions') }), _jsx("div", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "srOnly", "aria-hidden": !onWhiteBoard || undefined, children: clockAnnouncement })] }));
});
export default AnalogClock;
